import type {Question} from './types'

export const FALLBACK_QUESTION_POOL: Question[] = [
  {
    id: 'tf-01-cards-or-transfers-who-loses-more',
    type: 'true-false',
    shortLabel: 'Credit transfer losses',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question: 'Card payments account for a higher total fraud value in the EU than credit transfers. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'False',
    mossInsight: {
      body: "Most people assume card fraud is the main event. It isn't. Cards get hit more often but each hit is small. Credit transfers in the EU get hit less often and for much bigger sums, which is why they top the total loss table.",
      howMossHelps:
        'Pay with Moss and virtual corporate cards route every payment through a controlled, transparent system. Full approval logic, real-time visibility, and a complete audit trail from first click to final posting.',
    },
  },
  {
    id: 'tf-02-germany-still-loves-cash',
    type: 'true-false',
    shortLabel: 'Germans love cash',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question: 'In Germany, half of all point-of-sale transactions are still made in cash. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'Half of Germany still pays for their Döner with a crumpled tenner. Half of all point-of-sale transactions in Germany are still made in cash, according to the Bundesbank.',
      howMossHelps:
        'Moss replaces manual and cash-based processes with digital corporate payments that are fully visible, categorized, and governed through card limits, time restrictions, and real-time tracking.',
    },
  },
  {
    id: 'tf-03-the-reverse-charge-flip',
    type: 'true-false',
    shortLabel: 'Reverse charge VAT',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'Under the EU reverse charge mechanism, the supplier charges VAT on the invoice and the customer reclaims it. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'False',
    mossInsight: {
      body: 'The logic is completely flipped. Under reverse charge, no VAT appears on the invoice at all. The buyer declares and pays it on their own VAT return, even though nothing was charged. Get this wrong and you are not just facing a posting error. You are looking at a compliance issue.',
      howMossHelps:
        'Moss uses AI-supported pre-accounting to suggest the correct VAT treatment, including reverse charge scenarios, based on invoice content and past transactions. Fewer posting errors, less exposure.',
    },
  },
  {
    id: 'tf-04-the-deepfake-cfo-call',
    type: 'true-false',
    shortLabel: 'Deepfake wire fraud',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'Voice-cloning technology has already been used in real business cases to impersonate executives and authorize fraudulent wire transfers. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'In early 2024, a finance employee in Hong Kong was tricked into transferring approximately USD 25 million after joining a video call where everyone on screen, including the CFO, was AI-generated. This is not a future risk. It has already happened, and the technology is getting cheaper and easier to use.',
      howMossHelps:
        'Moss enforces structured payment workflows with predefined approvals. No single phone call or video conference can bypass the verification chain before money moves.',
    },
  },
  {
    id: 'tf-05-the-everyday-expense-steal',
    type: 'true-false',
    shortLabel: 'Expense reimbursement fraud',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'Expense reimbursement fraud is one of the most frequently occurring forms of employee fraud in companies worldwide. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'A £40 dinner here, a £15 taxi there, a receipt that is technically real. Expense fraud is the slow leak: about 1 in 8 cases overall per the ACFE, 1 in 5 in small businesses, fourth place behind corruption, billing and non-cash. The red flag rarely lives in one claim. It lives in the pattern.',
      howMossHelps:
        'Moss connects expense submissions to employee profiles, predefined budgets, and approval workflows, making it straightforward to spot unusual patterns and block unverified claims before they are paid.',
    },
  },
  {
    id: 'tf-06-who-actually-catches-fraud',
    type: 'true-false',
    shortLabel: 'Tip-off detection',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'Most workplace fraud, meaning fraud where an employee steals from or deceives their own employer, is initially caught by internal audits. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'False',
    mossInsight: {
      body: "The top way fraud gets caught isn't an audit. It's a tip. Per the ACFE 2024 Report, tips catch 43% of cases, internal audits just 14%, and more than half of those tips come from colleagues. Your team is statistically your best fraud detector. Reassuring and a little unsettling.",
      howMossHelps:
        'Moss creates a complete, searchable audit trail for every transaction and approval, giving finance teams the visibility to notice anomalies without relying on someone to come forward first.',
    },
  },
  {
    id: 'tf-07-the-hidden-fingerprint-in-the-numbers',
    type: 'true-false',
    shortLabel: "Benford's Law signal",
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'In naturally occurring financial datasets, the digit 9 is statistically the most common first digit of a transaction amount. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'False',
    mossInsight: {
      body: "Benford's Law states that the digit 1 is the most common leading digit, appearing around 30% of the time in organic financial data. When auditors see too many transactions starting with 7, 8, or 9, that deviation flags a potential manipulation. Fraudsters tend to pick numbers that feel random to humans but do not follow the natural distribution.",
      howMossHelps:
        'With all transactions centralized in Moss, finance teams can analyze spending patterns across vendors and time periods, making it easier to spot statistically unusual distributions that manual review would miss.',
    },
  },
  {
    id: 'tf-08-loyal-but-dangerous',
    type: 'true-false',
    shortLabel: 'Trusted insider risk',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'Internal fraud is typically committed by employees who have been with a company for less than one year. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'False',
    mossInsight: {
      body: 'The data tells the opposite story. Employees with more than five years of tenure cause the highest median fraud losses. The longer someone has been around, the more access they have, the more trust they have built up, and the better they understand exactly where the gaps are.',
      howMossHelps:
        'Moss enforces role-based access controls and separation of duties so that even highly trusted, long-tenured employees cannot execute and approve their own transactions without an independent second check.',
    },
  },
  {
    id: 'tf-09-sepa-once-it-s-gone-it-s-gone',
    type: 'true-false',
    shortLabel: 'SEPA irreversibility',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'SEPA credit transfers are reversible within 10 business days if fraud is reported promptly. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'False',
    mossInsight: {
      body: "Unlike card payments, which have chargeback mechanisms built in, SEPA credit transfers are generally not reversible once processed. Recovery depends entirely on the receiving bank's cooperation and, often, law enforcement involvement. This is exactly why pre-payment controls matter far more than post-payment recovery.",
      howMossHelps:
        'With Moss, payments go through structured approval flows before execution. Fraudulent or erroneous transfers are stopped before they happen, not chased after the money is already gone.',
    },
  },
  {
    id: 'tf-10-duplicate-invoices-always-deliberate',
    type: 'true-false',
    shortLabel: 'Duplicate invoice error',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question: 'Duplicate invoices are always the result of intentional fraud. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'False',
    mossInsight: {
      body: 'Most duplicate invoices are actually accidental, caused by vendors resubmitting through a different channel or internal process gaps across departments. Whether intentional or not, the financial impact is identical. Companies without systematic duplicate checks routinely overpay without ever realizing it.',
      howMossHelps:
        'Moss automatically flags potential duplicate invoices based on amount, vendor, date, and document number. Double payments get stopped before processing, regardless of whether the cause is fraud or just a workflow error.',
    },
  },
  {
    id: 'tf-11-america-s-favorite-fraud-target',
    type: 'true-false',
    shortLabel: 'Check fraud dominance',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'In the world biggest economy, the United States, paper checks remain the payment method most frequently targeted by fraud attempts. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'Checks: still the reigning champion of US payment fraud. The 2025 AFP Survey clocks 63 percent of organizations hit last year, ahead of every other rail, with 3 in 4 still mailing checks untracked. A 17th-century payment instrument, a 2026 problem.',
      howMossHelps:
        'Moss eliminates paper-based payment exposure entirely. Every disbursement is digital, traceable, and protected by approval chains. There is no envelope to steal.',
    },
  },
  {
    id: 'tf-12-crypto-gets-stolen-rarely-returned',
    type: 'true-false',
    shortLabel: 'Crypto theft recovery',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'More than 90 percent of cryptocurrency stolen in hacks is eventually recovered by law enforcement. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'False',
    mossInsight: {
      body: 'Not even close. USD 2.2 billion was stolen from crypto platforms in 2024 alone, with North Korean hackers taking 61 percent of that. Recovery rates remain very low, typically in the single digits. Once funds hit mixers, bridges, or privacy coins, the trail goes cold fast.',
      howMossHelps:
        'Moss operates in regulated banking rails, not crypto. Every transaction is traceable, auditable, and tied to verified counterparties, which is the opposite of the environment where stolen crypto disappears.',
    },
  },
  {
    id: 'tf-13-paying-the-ransom-invites-the-next-one',
    type: 'true-false',
    shortLabel: 'Ransomware repeat attacks',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'Organizations that pay a ransomware  demand (malware that locks or encrypts your files and demands payment to restore access) are statistically more likely to be attacked again. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'Paying does not buy peace. It marks your company as someone who pays. Security research consistently shows that organizations that paid a ransom are disproportionately hit again, often within 12 months and sometimes by the same group rebranded. Paying also does not guarantee data recovery.',
      howMossHelps:
        "Attackers often follow a ransomware incident with fake urgent payment requests to a rattled finance team. Moss keeps vendor verification and approval workflows in place regardless of what else is on fire, so a panicking email doesn't become a wire.",
    },
  },
  {
    id: 'tf-14-your-fraudster-has-a-clean-record',
    type: 'true-false',
    shortLabel: 'Your Fraudster Has A Clean Record',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'The typical internal fraudster is a first-time offender with no previous fraud or criminal history. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'The mental image of a career criminal is almost always wrong. According to the ACFE, the large majority of occupational fraudsters have a spotless record before they commit the act. They are usually the reliable, long-tenured colleague that nobody would suspect. That is part of the reason controls matter more than background checks. You are protecting against future behavior, not past records.',
      howMossHelps:
        'Moss builds its controls around process, not reputation. Separation of duties, approval chains, and complete audit trails apply to every user, trusted or not.',
    },
  },
  {
    id: 'tf-15-a-record-billion-euro-gdpr-fine',
    type: 'true-false',
    shortLabel: 'Meta GDPR penalty',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question: 'The largest single GDPR fine ever issued exceeded one billion euros. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'Meta was fined 1.2 billion euros by the Irish Data Protection Commission in May 2023 for transferring EU user data to the US without adequate safeguards, the largest GDPR penalty on record. Regulators are not bluffing anymore, and the trend is up, not down.',
      howMossHelps:
        'Moss processes financial and employee data under strict GDPR and EU data-protection standards, with controlled access and EU-based processing to keep your exposure inside the regulatory guardrails.',
    },
  },
  {
    id: 'tf-16-instant-payments-are-now-a-legal-default',
    type: 'true-false',
    shortLabel: 'Instant Payments Regulation',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'Since October 2025, all euro-area payment service providers must offer to send instant SEPA credit transfers at no higher cost than regular transfers. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'The EU Instant Payments Regulation completed its phased rollout in October 2025. Euro-area banks and PSPs must receive instant payments (since January 2025), send them (since October 2025), and cannot charge more for instant than for standard transfers. Speed is no longer a premium product.',
      howMossHelps:
        'Moss moves supplier and employee payments through the fastest compliant rail available, with full approval controls layered on top, so speed does not come at the cost of visibility.',
    },
  },
  {
    id: 'tf-17-name-must-match-iban-or-it-fails',
    type: 'true-false',
    shortLabel: 'SEPA VoP mandate',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'Since 9 October 2025, eurozone banks must check that the beneficiary name matches the IBAN before executing a SEPA credit transfer. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: "This is one of the biggest anti-fraud changes in European payments in a decade. Verification of Payee is now mandatory and free for users. Before a euro leaves, the payer's bank pings the payee's bank and gets back Match, Close Match, No Match, or Unable to Verify. Invoice redirection and CEO fraud just got structurally harder.",
      howMossHelps:
        'Moss stores verified supplier data centrally, so your AP flow is already aligned with Verification of Payee logic. Bank details and names live together, with changes tracked and flagged.',
    },
  },
  {
    id: 'tf-18-who-owns-that-company-fewer-people-can-check',
    type: 'true-false',
    shortLabel: 'EU beneficial ownership access',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'Central beneficial ownership registers across the EU are freely accessible to the general public. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'False',
    mossInsight: {
      body: 'They used to be, until the Court of Justice of the EU struck that down in November 2022. General public access was ruled a disproportionate interference with privacy. Access is now limited to competent authorities, obliged entities, and those who can demonstrate a legitimate interest, which makes supplier due diligence meaningfully harder.',
      howMossHelps:
        'Moss keeps vendor data where you can actually use it. Onboarding captures the fields your risk assessment needs once, in one place, instead of someone rebuilding the picture from old emails every time.',
    },
  },
  {
    id: 'tf-19-four-eyes-fewer-surprises',
    type: 'true-false',
    shortLabel: 'Segregation of duties',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'The "four eyes principle" means that two different people must review or approve a sensitive action, such as releasing a payment, before it goes through.\n\nTrue or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'One person is a risk. Two in on it is a conspiracy. Splitting invoice creation, approval, and payment release across different people is the oldest trick in the fraud prevention book, and still the most reliable. Every major fraud framework, from COSO to ISO 37001, leans on this idea.',
      howMossHelps:
        'Moss bakes the four eyes principle into payment workflows by default. Approvers, spenders, and payment executors can be different roles, with every step logged and attributable.',
    },
  },
  {
    id: 'tf-20-every-euro-of-fraud-hurts-four-times',
    type: 'true-false',
    shortLabel: 'LexisNexis cost multiplier',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'Research indicates that for every 1 dollar of fraud loss, US and European financial services firms absorb roughly 4 dollars in total cost (investigations, legal fees, recovery work, lost productivity). True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'The stolen amount is almost never the real cost. Every euro lost pulls in investigation hours, legal fees, recovery work, and a pile of operational chaos. Research consistently puts the all in cost at three to five times the face value. Fraud loss is an iceberg. The headline number is the tip.',
      howMossHelps:
        'Moss reduces not just direct fraud loss but the hidden cost around it: fewer reconciliations, less manual investigation, and cleaner audit trails when something does need explaining.',
    },
  },
  {
    id: 'tf-21-the-most-common-fraud-is-also-the-quietest',
    type: 'true-false',
    shortLabel: 'The Most Common Fraud Is Also the Quietest',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'Employees stealing or misusing company assets (things like cash, inventory, or expense reimbursements) is the most common type of workplace fraud, showing up in nearly 9 out of 10 cases. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'Asset misappropriation is the bread and butter of occupational fraud. Think small theft, padded expenses, the missing laptop, the vendor that never delivered. Individually it looks petty. The problem is the pattern. A few euros a week, for years, turns into a number that would have set off every alarm if it had happened at once.',
      howMossHelps:
        'Moss makes asset misappropriation structurally harder by linking every card and payment to a named user, a budget, and an approval trail. There is no anonymous path for small, repeated theft.',
    },
  },
  {
    id: 'tf-22-the-deepfake-explosion',
    type: 'true-false',
    shortLabel: 'Deepfake attempt growth',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'The number of deepfake files circulating online grew roughly 16 fold between 2023 and 2025. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'Around 500,000 known deepfake files existed in 2023. By 2025 the count had jumped to 8 million. That is not a linear curve, it is a hockey stick. The tools got cheaper, the quality got better, and cloning a voice or face dropped from specialist work to a weekend project. Your finance controls now have to assume a video call or voice memo can lie.',
      howMossHelps:
        'Moss adds structural friction to AI-enabled social engineering. Payments require workflow approval that no single deepfake call or video can bypass, no matter how convincing.',
    },
  },
  {
    id: 'tf-23-quishing-the-qr-code-is-the-new-hook',
    type: 'true-false',
    shortLabel: 'QR code phishing growth',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'QR code phishing (so-called quishing) has become one of the fastest-growing credential-theft vectors in corporate environments. True or False?',
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'Email filters are good at stopping dodgy links. They are much worse at reading images of QR codes. Attackers hide the malicious URL behind a QR, the user scans it on a phone (outside corporate controls), and lands on a credential page.',
      howMossHelps:
        'Moss payments never rely on a scanned code. Every action is initiated and approved inside the platform with verified identities, so a QR code cannot substitute for a real approval.',
    },
  },
  {
    id: 'tf-24-black-friday-s-evil-twin',
    type: 'true-false',
    shortLabel: "Black Friday's Evil Twin",
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      "Thanksgiving Day has ranked as the single biggest day for attempted online fraud in the US, Canada, and the UK, even though two of those countries don't celebrate it. True or False?",
    options: ['True', 'False'],
    correctAnswer: 'True',
    mossInsight: {
      body: 'Fraudsters follow the shopping calendar, not the national calendar. A TransUnion analysis found Thanksgiving Day topped the charts for attempted online fraud across the US, Canada, and the UK. Consumers rushing through checkouts, inboxes packed with deals. Perfect weather for a scam.',
      howMossHelps:
        'Finance teams hit their own version of this around year end. Closings, holiday cover, last minute vendor invoices all landing at once. Moss keeps approvals consistent through the rush, so a hectic December looks procedurally the same as a quiet March.',
    },
  },
  {
    id: 'tf-25-sms-codes-convenient-not-secure',
    type: 'true-false',
    shortLabel: 'SMS MFA weakness',
    phase: 1,
    questionGroup: 'p1-true-false',
    timerSeconds: 30,
    points: 6,
    question:
      'SMS-based two-factor authentication is considered the strongest form of multi-factor authentication for corporate finance systems.\n\nTrue or False?',
    options: ['True', 'False'],
    correctAnswer: 'False',
    mossInsight: {
      body: 'Here is the uncomfortable bit. SMS codes are better than nothing, but they are the junk food of authentication. A SIM swap, where a scammer sweet talks your mobile carrier into porting your number to their phone, bypasses the whole thing. The real options are authenticator apps or a hardware key.',
      howMossHelps:
        "Moss protects access with strong multi factor authentication and granular role based permissions. Even if an employee's email password leaks, no one can log in, open a payment, and approve it alone. The payment flow has its own set of locks.",
    },
  },
  {
    id: 'fi-01-how-big-is-europe-s-fraud-problem',
    type: 'fermi',
    shortLabel: 'Europe fraud losses',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'Estimate the total annual fraud losses across Europe, including consumer, business, and unreported fraud. Answer in billion USD.',
    options: [],
    correctAnswer: '100',
    fermiRange: [80, 120],
    fermiInputPlaceholder: 'Enter a number in billion USD',
    fermiRangeSuffix: 'billion USD',
    mossInsight: {
      body: 'Think of it as an iceberg. The fraud cases that get reported and end up in official statistics? That is the tip. Underneath, unreported incidents and indirect costs push total European fraud losses to an estimated $100 billion a year. Most of it never shows up anywhere.',
      howMossHelps:
        'As fraud scales, Moss brings all invoices, payments, and transaction history into one place: real-time anomaly detection, supplier comparison, and controlled approval flows before money leaves the company.',
    },
  },
  {
    id: 'fi-02-fake-euros-still-circulating',
    type: 'fermi',
    shortLabel: 'Counterfeit banknotes',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'How many counterfeit euro banknotes were withdrawn from circulation in Germany in the first half of 2025? Enter an approximate number.',
    options: [],
    correctAnswer: '36600',
    fermiRange: [20000, 60000],
    fermiInputPlaceholder: 'Enter an approximate number',
    fermiRangeSuffix: 'notes',
    mossInsight: {
      body: "Even inside one of the most tightly regulated currency zones on the planet, tens of thousands of fake euro notes get pulled out of Germany's cash flow every six months. The 20 and 50 euro notes are the forger's favourites.",
      howMossHelps:
        'Moss replaces cash with digital corporate payments where every transaction is traceable, categorized, and visible in real time. There are no fake notes in a system where nothing physical changes hands.',
    },
  },
  {
    id: 'fi-03-fraud-is-not-just-growing-it-s-accelerating-growth-percentage',
    type: 'fermi',
    shortLabel: 'Digital fraud surge',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'According to the 2024 Tietoevry Banking Insight report analyzing 3.7 billion European bank transactions, by approximately what percentage did attempted digital payment fraud increase year-on-year in Europe? Enter a percentage.',
    options: [],
    correctAnswer: '43',
    fermiRange: [35, 51],
    fermiInputPlaceholder: 'Enter a percentage',
    fermiRangeSuffix: '%',
    mossInsight: {
      body: 'Tietoevry tracked 3.7 billion transactions on behalf of European banks in 2024 and saw attempted fraud climb around 43 percent year on year. The direction is clear: controls that stayed still last year are effectively weaker this year.',
      howMossHelps:
        'As fraud scales, Moss gives finance teams full control before money moves: approval workflows, real-time visibility, and centralized payment data to catch anything unusual early.',
    },
  },
  {
    id: 'fi-04-a-year-of-bleeding-time-until-internal-fraud-is-noticed',
    type: 'fermi',
    shortLabel: '12-month blind spot',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'On average, how many months does it take before internal employee fraud at a company is discovered? Enter a number.',
    options: [],
    correctAnswer: '12',
    fermiRange: [10, 14],
    fermiInputPlaceholder: 'Enter a number',
    fermiRangeSuffix: 'months',
    mossInsight: {
      body: 'A full year. Research consistently puts the median detection time for occupational fraud at 12 months. Every month without detection compounds the total loss. And by the time internal audit catches it, the damage is already locked in.',
      howMossHelps:
        'Moss gives finance teams real-time visibility into all transactions, approvals, and spending patterns. That closes the detection gap from months to days.',
    },
  },
  {
    id: 'fi-05-the-small-business-price-tag-medium-fraud-loss-per-company',
    type: 'fermi',
    shortLabel: 'Median fraud loss',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'What is the median financial loss per case of internal employee fraud (fraud committed by employees against their own company) globally, according to leading fraud research? Answer in USD.',
    options: [],
    correctAnswer: '145000',
    fermiRange: [100000, 190000],
    fermiInputPlaceholder: 'Enter a number in USD',
    fermiRangeSuffix: 'USD',
    mossInsight: {
      body: 'The global median loss per fraud case is $145,000. For smaller companies it tends to hit harder because of fewer controls, less redundancy, and no buffer to absorb the damage. One case can be enough to cause serious financial distress.',
      howMossHelps:
        'Moss gives growing companies enterprise-grade spend controls without the enterprise overhead. Approval chains, budget limits, and audit trails work at any team size.',
    },
  },
  {
    id: 'fi-06-phishing-s-real-share-of-breaches',
    type: 'fermi',
    shortLabel: 'Phishing breach rate',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'According to the most recent Verizon Data Breach Investigations Report 2025, phishing was used as an initial access method in approximately what percentage of confirmed data breaches? Enter a percentage.',
    options: [],
    correctAnswer: '16',
    fermiRange: [10, 24],
    fermiInputPlaceholder: 'Enter a percentage',
    fermiRangeSuffix: '%',
    mossInsight: {
      body: 'The "90 percent of attacks start with phishing" line is marketing, not data. In the latest Verizon DBIR (2025), phishing shows up in about 16 percent of confirmed breaches. Stolen credentials lead at 22 percent. For finance teams the real headache is the laser targeted cousin, Business Email Compromise.',
      howMossHelps:
        "When payment instructions go through Moss's structured workflows rather than open email chains, there is no phishing email to intercept and no informal channel that bypasses verification.",
    },
  },
  {
    id: 'fi-07-germany-s-billion-invoice-mandate-digital-invoicing',
    type: 'fermi',
    shortLabel: 'B2B invoice mandate',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'In which year did it become mandatory for all German businesses to be able to receive structured B2B e-invoices? Enter a year.',
    options: [],
    correctAnswer: '2025',
    fermiRange: [2025, 2025],
    fermiInputPlaceholder: 'Enter a year',
    mossInsight: {
      body: "Germany's B2B e-invoicing mandate started on 1 January 2025, but only the obligation to RECEIVE structured e-invoices applies immediately. Issuing becomes mandatory on 1 January 2027 for\ncompanies with turnover above 800,000 euros and on 1 January 2028 for the rest.",
      howMossHelps:
        'Moss supports structured digital invoice receipt and processing, keeping teams aligned with e-invoicing requirements as regulations tighten across the EU.',
    },
  },
  {
    id: 'fi-08-what-bec-costs-the-business-world',
    type: 'fermi',
    shortLabel: 'BEC financial losses\n\n-',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'BEC fraud (when fraudsters impersonate executives or suppliers via email to steal money) is one of the costliest cybercrime categories tracked by the FBI. What were total reported BEC losses in the US in 2024? Answer in billion USD.',
    options: [],
    correctAnswer: '2.77',
    fermiRange: [2, 3.5],
    fermiInputPlaceholder: 'Enter a number in billion USD',
    fermiRangeSuffix: 'billion USD',
    mossInsight: {
      body: 'AI-generated BEC content is a fast-rising share, reported at\naround 40 percent by VIPRE in Q2 2024 and around 14 percent by Barracuda in April 2025, varying by vendor and methodology.',
      howMossHelps:
        'Moss makes it structurally harder to act on a fraudulent email. Payments require workflow approval and IBAN changes are tied to verified vendor records, not to what someone wrote in a message.',
    },
  },
  {
    id: 'fi-09-the-true-price-tag-of-a-data-breach',
    type: 'fermi',
    shortLabel: 'Data breach cost',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'According to the most recent IBM Cost of a Data Breach Report 2025, what is the current global average cost of a single data breach?\n\nAnswer in million USD.',
    options: [],
    correctAnswer: '4.44',
    fermiRange: [3, 6],
    fermiInputPlaceholder: 'Enter a number in million USD',
    fermiRangeSuffix: 'million USD',
    mossInsight: {
      body: 'The global average data breach cost currently stands at $4.44 million per incident globally, though for US companies it has hit an all-time high of $10.22 million. That number covers detection and escalation, notification, post-breach response (including regulatory fines and legal costs), and lost business. A breach involving financial data or payment systems typically lands at the higher end.',
      howMossHelps:
        'Moss reduces the financial data attack surface by centralizing transaction flows through controlled, audited workflows with role-based access, minimizing the risk of a breach affecting payment or vendor data.',
    },
  },
  {
    id: 'fi-10-how-much-vat-just-vanishes-in-the-eu',
    type: 'fermi',
    shortLabel: 'EU VAT gap',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      "According to the European Commission's VAT Gap Report in 2025, how much VAT revenue was lost across EU member states in 2023? Answer in billion euros.",
    options: [],
    correctAnswer: '128',
    fermiRange: [100, 150],
    fermiInputPlaceholder: 'Enter a number in billion euros',
    fermiRangeSuffix: 'billion euros',
    mossInsight: {
      body: 'he VAT Gap is the yawning space between the VAT governments should collect and what actually lands in the treasury. The latest European Commission report puts it at €128 billion across the EU, a €27 billion jump in a single year. Some is admin error. A large slice is deliberate carousel fraud, where VAT gets reclaimed on transactions that never happened.',
      howMossHelps:
        'Moss uses AI-supported pre-accounting to suggest correct VAT treatment on invoices, reducing unintentional errors and the kind of VAT processing gaps that compliance audits flag.',
    },
  },
  {
    id: 'fi-11-the-wirecard-hole-that-wasn-t-cash',
    type: 'fermi',
    shortLabel: 'Wirecard missing balance',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'When German payments giant Wirecard collapsed in 2020, how much cash listed on its balance sheet was declared not to exist? Answer in billion euros.',
    options: [],
    correctAnswer: '1.9',
    fermiRange: [1, 2.8],
    fermiInputPlaceholder: 'Enter a number in billion euros',
    fermiRangeSuffix: 'billion euros',
    mossInsight: {
      body: "Europe's biggest accounting scandal of the decade. Wirecard's auditors could not confirm 1.9 billion euros of escrowed cash that the company claimed sat in trust accounts in the Philippines. The money did not exist. The DAX constituent filed for insolvency days later, and the ripple effects reshaped BaFin, KPMG, and the EU audit regulation debate.",
      howMossHelps:
        "Moss does not audit a company's books, but it closes the gap where fraud most often hides in growing companies: ad hoc spend, off platform payments, and unreconciled supplier invoices. Every transaction sits in one system, traceable end to end.",
    },
  },
  {
    id: 'fi-12-cash-in-bin-bags',
    type: 'fermi',
    shortLabel: 'Cash in Bin Bags',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'A major UK high street bank became the first to be criminally convicted under the UK Money Laundering Regulations after a Bradford jewellery business funnelled hundreds of millions in cash through its accounts, including black bin bags of notes with around £700,000 deposited on a single day. What was the total fine imposed on the bank? Answer in million GBP.',
    options: [],
    correctAnswer: '265',
    fermiRange: [200, 330],
    fermiInputPlaceholder: 'Enter a number in million GBP',
    fermiRangeSuffix: 'million GBP',
    mossInsight: {
      body: 'The customer was forecast to deposit £15 million a year. It deposited more than £365 million, of which £264 million was physical cash. The bank\'s systems flagged the pattern repeatedly and no one acted. The case is now the UK reference point for "your alerts are only worth what you do with them."',
      howMossHelps:
        'Moss does not replace AML monitoring, but it removes a failure mode behind cases like this: off platform cash and unmonitored vendor payments. Every supplier transaction in Moss sits in a single audit trail, visible to the right approver.',
    },
  },
  {
    id: 'fi-13-the-hong-kong-deepfake-heist',
    type: 'fermi',
    shortLabel: 'Arup deepfake loss',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'In the 2024 Arup case in Hong Kong, how much did a finance employee transfer after joining a AI-generated video call with a fake CFO and colleagues?\n\nAnswer in million USD.',
    options: [],
    correctAnswer: '25',
    fermiRange: [10, 40],
    fermiInputPlaceholder: 'Enter a number in million USD',
    fermiRangeSuffix: 'million USD',
    mossInsight: {
      body: 'The employee believed they were on a normal call. Everyone on the screen, including the CFO, was a deepfake. Fifteen separate transfers were made before the fraud was caught. Arup confirmed the case publicly and it remains the single highest-profile deepfake business fraud to date.',
      howMossHelps:
        'No single call, no matter how real, moves money in Moss. Payments live inside structured workflows with independent approvers who see the same verified data.',
    },
  },
  {
    id: 'fi-14-crypto-platforms-under-siege',
    type: 'fermi',
    shortLabel: 'Chainalysis stolen funds',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'How much cryptocurrency was stolen from crypto platforms globally in 2025, according to Chainalysis?\n\nAnswer in billion USD.',
    options: [],
    correctAnswer: '3.4',
    fermiRange: [2.5, 4.5],
    fermiInputPlaceholder: 'Enter a number in billion USD',
    fermiRangeSuffix: 'billion USD',
    mossInsight: {
      body: 'In 2025, over $3.4 billion in cryptocurrency was stolen. A massive portion of this total came from a single attack on Bybit in February, which accounted for $1.5 billion alone. The data show a growing trend where massive outlier hacks are driving the majority of annual losses.',
      howMossHelps:
        'Moss operates on regulated banking rails and does not custody crypto. Your working capital sits in audited, insured, and compliant infrastructure.',
    },
  },
  {
    id: 'fi-15-ransomware-paying-vs-refusing',
    type: 'fermi',
    shortLabel: 'Ransomware Response Trends',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      "According to IBM's 2025 cyber study, what share of organisations hit by ransomware refused to pay the ransom? Enter a percentage.",
    options: [],
    correctAnswer: '63',
    fermiRange: [55, 70],
    fermiInputPlaceholder: 'Enter a percentage',
    fermiRangeSuffix: '%',
    mossInsight: {
      body: "The tide is turning, slowly. IBM's 2025 study found 63 percent of ransomware-hit organisations chose not to pay. 37 percent still did. The shift matters because paying funds the next attack and statistically raises the odds of being hit again. Refusal is now the majority response.",
      howMossHelps:
        'Moss does not stop a ransomware attack, but payments in Moss always need independent approval from another verified user. Even if one account is compromised, a single user alone cannot push money out the door.',
    },
  },
  {
    id: 'fi-16-the-half-billion-slap',
    type: 'fermi',
    shortLabel: 'Largest single AML fine 2025',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'How large was the single biggest anti money laundering fine issued to a cryptocurrency exchange in 2025? Answer in million USD.',
    options: [],
    correctAnswer: '504',
    fermiRange: [400, 650],
    fermiInputPlaceholder: 'Enter a number in million USD',
    fermiRangeSuffix: 'million USD',
    mossInsight: {
      body: 'Crypto exchange OKX pleaded guilty in February 2025 to running an unlicensed money transmitting business in the US and agreed to roughly $504 million in combined penalties. Regulators cited millions of users onboarded without proper KYC, traders in sanctioned jurisdictions who were never geo blocked, and billions in suspicious transaction volume that never triggered a single suspicious activity report.',
      howMossHelps:
        'Moss is a spend platform with clean structured records for every vendor, invoice, and payment. The evidence regulators and auditors increasingly expect is there by default, not assembled in a scramble after the fact.',
    },
  },
  {
    id: 'fi-17-global-card-fraud-one-number',
    type: 'fermi',
    shortLabel: 'Nilson card fraud total',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'According to the Nilson Report, what were total global card fraud losses (credit, debit, and prepaid) in the most recent full year of data (2024)?\n\nAnswer in billion USD.',
    options: [],
    correctAnswer: '33',
    fermiRange: [23, 43],
    fermiInputPlaceholder: 'Enter a number in billion USD',
    fermiRangeSuffix: 'billion USD',
    mossInsight: {
      body: 'That is card fraud only. Card not present transactions drive the bulk of it. Nilson projects losses to continue climbing as e-commerce volume grows, even as issuers push 3D Secure and tokenization. The losses are distributed across merchants, acquirers, issuers, and ultimately cardholders.',
      howMossHelps:
        'Moss virtual cards are issued for specific vendors or purposes, with configurable limits and validity windows. A leaked number has a tiny blast radius.',
    },
  },
  {
    id: 'fi-18-the-ransom-is-not-the-bill',
    type: 'fermi',
    shortLabel: 'Ransomware incident cost',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      "According to IBM's 2025 cyber study, what is the average total cost of a ransomware or extortion incident when an attacker publicly discloses the breach? Answer in million USD.",
    options: [],
    correctAnswer: '5.08',
    fermiRange: [3.5, 7],
    fermiInputPlaceholder: 'Enter a number in million USD',
    fermiRangeSuffix: 'million USD',
    mossInsight: {
      body: 'Even when companies refuse to pay, the bill still arrives. IBM puts the average total cost of a disclosed ransomware incident at $5.08 million in 2025. That figure is downtime, investigation, legal, customer notification, and the quiet reputational bruise that shows up in the next sales cycle. The ransom itself is often the smallest line.',
      howMossHelps:
        'Moss runs in the cloud with a complete audit trail of every invoice, approval, and payment. If an attack takes out local systems, the finance record is still intact and the payment workflow keeps running independently of the office hardware.',
    },
  },
  {
    id: 'fi-19-pig-butchering-s-global-toll',
    type: 'fermi',
    shortLabel: 'Pig butchering total losses',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'A 2024 University of Texas study tracked the total global flow of cryptocurrency into so-called pig butchering scams (long term romance investment fraud, often run out of Southeast Asian compounds by workers who are themselves human trafficking victims) between 2020 and early 2024. Estimate the total amount in billion USD.',
    options: [],
    correctAnswer: '75',
    fermiRange: [55, 95],
    fermiInputPlaceholder: 'Enter a number in billion USD',
    fermiRangeSuffix: 'billion USD',
    mossInsight: {
      body: 'A number few people outside enforcement even heard of four years ago. University of Texas finance professor John Griffin and Kevin Mei traced blockchain addresses across 4,000 victims and four years to build the estimate. Most scammers operate from Southeast Asia, and a large share of the perpetrators are themselves trafficking victims. It is both a financial and a humanitarian scandal.',
      howMossHelps:
        'Corporate money in Moss does not travel through opaque crypto rails. Every payment has a verified counterparty, a documented reason, and a full audit log.',
    },
  },
  {
    id: 'fi-20-crypto-pays-the-biggest-price',
    type: 'fermi',
    shortLabel: 'Top AML fined sector 2025',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'How much did cryptocurrency firms pay globally in anti money laundering fines in 2025? Answer in billion USD.',
    options: [],
    correctAnswer: '1',
    fermiRange: [0.7, 1.5],
    fermiInputPlaceholder: 'Enter a number in billion USD',
    fermiRangeSuffix: 'billion USD',
    mossInsight: {
      body: 'Crypto overtook banking as the most heavily fined sector for anti money laundering failures in 2025, with first half fines alone crossing $927 million. Behind crypto in the ranking: banking at $200M+, payments and FinTech at $160M+, trading and brokerage near $50M, gambling at $22M+. Shortcuts at scale get punished at scale.',
      howMossHelps:
        'Every euro moving through Moss leaves a clean trail. Named approvers on every payment, documented vendor records, and a full history of IBAN changes. Visibility that holds up whether internal audit, the tax office, or the CFO comes asking.\n\nhttps://complyadvantage.com/insights/the-biggest-aml-fines-in-2025/',
    },
  },
  {
    id: 'fi-21-fraud-doesn-t-care-about-your-age',
    type: 'fermi',
    shortLabel: 'Fraud Demographics',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'By roughly what percentage are adults aged 18 to 59 more likely than people 60 and over to report losing money to fraud? Enter a percentage.',
    options: [],
    correctAnswer: '34',
    fermiRange: [25, 45],
    fermiInputPlaceholder: 'Enter a percentage',
    fermiRangeSuffix: '%',
    mossInsight: {
      body: 'Plot twist. The demographic most associated with fraud victimhood, older adults, actually reports losing money less often. Data shows that people aged 18 to 59 are about 34 percent more likely than those 60 and over to report a fraud loss. Digital natives are not immune. Anyone moving money online is a target.',
      howMossHelps:
        'Employees using Moss sit inside a payment system with built in approval flows, vendor verification, and audit trails. Even the scam susceptible among us have structural guardrails between a bad click and a real transfer.',
    },
  },
  {
    id: 'fi-22-the-biggest-ponzi-in-history',
    type: 'fermi',
    shortLabel: 'Ponzi Scheme Scale',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      "How much did Bernie Madoff's Wall Street investment firm defraud investors of, making it the largest Ponzi scheme ever uncovered? Answer in billion USD.",
    options: [],
    correctAnswer: '64.8',
    fermiRange: [45, 85],
    fermiInputPlaceholder: 'Enter a number in billion USD',
    fermiRangeSuffix: 'billion USD',
    mossInsight: {
      body: 'No real profits, no real investments, just a carousel of new investor money paying old investor returns for decades. When $7 billion in redemptions hit at once in 2008, Madoff confessed to his sons, who turned him in the next day. The ledger holding all the fraudulent records sat on an IBM AS/400 server from the 1980s that Madoff refused to replace. Now we know why.',
      howMossHelps:
        'Moss payments run through verified counterparties and structured approvals on regulated banking rails. No black box, no promised magic returns, every transaction traceable end to end.',
    },
  },
  {
    id: 'fi-23-the-cost-of-one-paper-invoice',
    type: 'fermi',
    shortLabel: 'Invoice processing cost',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'According to AP benchmarking research, what is the approximate average cost to process a single invoice manually end to end? Answer in USD.',
    options: [],
    correctAnswer: '17',
    fermiRange: [10, 24],
    fermiInputPlaceholder: 'Enter a number in USD',
    fermiRangeSuffix: 'USD',
    mossInsight: {
      body: 'Seventeen dollars just to move one piece of paper through receipt, approval, matching, and posting. Automated best-in-class AP teams do the same for around 3 dollars. Multiply the gap by tens of thousands of invoices and the savings fund an entire finance team.',
      howMossHelps:
        'Moss automates invoice intake, pre-accounting, and approval, turning a 17-dollar process into something closer to click-and-post, without losing control.',
    },
  },
  {
    id: 'fi-24-the-six-thousand-dollar-shower-curtain',
    type: 'fermi',
    shortLabel: 'Tyco executive spending',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'During the Tyco accounting fraud, CEO Dennis Kozlowski used company funds to renovate his Fifth Avenue apartment. Roughly how much did his now famous shower curtain cost? Answer in USD.',
    options: [],
    correctAnswer: '6000',
    fermiRange: [3000, 10000],
    fermiInputPlaceholder: 'Enter a number in USD',
    fermiRangeSuffix: 'USD',
    mossInsight: {
      body: 'Tyco inflated income by $500 million and funnelled $150 million to its CEO and CFO through unapproved loans disguised as bonuses. The money paid for a $2 million Jimmy Buffett private island party, a $2,200 wastebasket, and the shower curtain that became the icon of early 2000s corporate excess. At that scale, extravagance is rarely just about taste. It tends to be the visible side of a much bigger control failure.',
      howMossHelps:
        'Moss keeps card spending on a short leash. Every employee card has configurable limits, budget categories, and approval flows, so there is no quiet path from a corporate card to a five figure impulse buy.',
    },
  },
  {
    id: 'fi-25-the-scale-of-global-phishing',
    type: 'fermi',
    shortLabel: 'Phishing email volume',
    phase: 1,
    questionGroup: 'p1-fermi',
    timerSeconds: 30,
    points: 14,
    question:
      'Roughly how many phishing emails are estimated to be sent globally every single day? Answer in billions.',
    options: [],
    correctAnswer: '3.4',
    fermiRange: [2, 5],
    fermiInputPlaceholder: 'Enter a number in billions',
    fermiRangeSuffix: 'billions',
    mossInsight: {
      body: 'About 3.4 billion phishing emails daily. That is nearly one for every two people alive. Most get filtered. A small fraction arrives. A smaller fraction gets clicked. And that tiny leakage is enough to power BEC, ransomware, and credential-theft campaigns worldwide.',
      howMossHelps:
        'Moss payment workflows are independent of email. A phished inbox does not hand over payment authority.',
    },
  },
  {
    id: 'sc-01-ceo-on-whatsapp-do-you-trust-this',
    type: 'multiple-choice',
    shortLabel: 'CEO WhatsApp scam',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'You get a WhatsApp message from your "CEO" while they are traveling. They say their hotel had to be booked through a private account and they need EUR 900 reimbursed via bank transfer right away. They are about to walk into a meeting and cannot take calls. What is the most appropriate action according to proper finance controls?',
    options: [
      'Check past hotel bookings and proceed if the request fits past behavior.',
      'Ask the CEO to confirm via reply message and process once they respond.',
      'Process the reimbursement given the urgency and document it afterward.',
      'Ask the CEO to go through the normal expense process, and double-check through a independant channel you already trust.',
    ],
    correctAnswer:
      'Ask the CEO to go through the normal expense process, and double-check through a independant channel you already trust.',
    mossInsight: {
      body: 'Urgency plus authority plus no real verification channel is the exact playbook of CEO fraud. This type of impersonation is one of the most financially damaging fraud types in business payments. Even smaller amounts are often used deliberately to test whether controls can be bypassed.',
      howMossHelps:
        'Moss only processes reimbursements through verified, employee-linked workflows with predefined approval steps and a full audit trail. A WhatsApp message cannot trigger a payment.',
    },
  },
  {
    id: 'sc-02-the-iban-switch-email',
    type: 'multiple-choice',
    shortLabel: 'Vendor IBAN switch',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'A long-standing supplier emails you to say their bank details have changed due to an internal restructuring. The email has the right logo and signature, references a real invoice number, matches their usual tone, and includes updated IBAN details. The next payment of EUR 12,400 is due in three days. What do you do?',
    options: [
      'Update the bank details. The supplier relationship exists and the request looks consistent.',
      'Reply to the email and request written confirmation on company letterhead first.',
      'Verify the bank detail change via an independent communication channel before making any changes.',
      'Wait for the next invoice to see whether the new IBAN appears again before updating.',
    ],
    correctAnswer: 'Verify the bank detail change via an independent communication channel before making any changes.',
    mossInsight: {
      body: 'The most dangerous fraud emails do not look suspicious. They look exactly like this. IBAN change requests are one of the highest-risk triggers in B2B payments. "It looks legit" is not a control. The only safe verification is one you initiated yourself, not one that came through the same channel as the request.',
      howMossHelps:
        'Moss keeps invoices, vendors, and IBAN history in one structured system with a full audit trail. A new account for an old vendor does not just appear on the next wire; it lands as a change against what was there before, for someone to actually look at.',
    },
  },
  {
    id: 'sc-03-ghost-employee-on-the-books',
    type: 'multiple-choice',
    shortLabel: 'Ghost employee payroll',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'During a quarterly review you discover that a former employee who left six months ago is still in payroll and has received six monthly salary payments totaling EUR 27,000. When you ask HR, the manager says offboarding was handled informally and the account was never properly deactivated. What is the correct course of action?',
    options: [
      'Post a corrective journal entry, recover the net overpayment through payroll, and flag it as an offboarding process gap for HR.',
      'Escalate to HR and Finance leadership, freeze any further payments, and treat it as a potential control failure until proven otherwise.',
      'Calculate the total overpayment, issue a formal repayment notice to the former employee, and document it for the next audit.',
      'Contact the former employee directly to agree on a repayment plan and keep the process moving without waiting for a full internal review.',
    ],
    correctAnswer:
      'Escalate to HR and Finance leadership, freeze any further payments, and treat it as a potential control failure until proven otherwise.',
    mossInsight: {
      body: 'Ghost employee fraud, whether intentional or negligent, is a serious internal control failure. Six months of unchecked salary payments with no offboarding verification is not a process gap to note and move on from. The median payroll fraud loss sits at roughly $100,000 per case before detection.',
      howMossHelps:
        'With Moss, employee-linked card accounts and payment permissions are tied to active user profiles. When an employee is deactivated, their access and associated instruments are revoked immediately.',
    },
  },
  {
    id: 'sc-04-round-numbers-raised-eyebrows',
    type: 'multiple-choice',
    shortLabel: 'Threshold manipulation',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      "Your monthly expense review shows that one team member submitted 9 reimbursement claims in the past quarter. Every single one falls between EUR 490 and EUR 499, just below your company's EUR 500 auto-approval threshold. All have receipts attached. What is the most appropriate response?",
    options: [
      'Approve them. Each claim is within policy, receipts are attached, and the amounts are not duplicates.',
      'Request additional documentation for each claim, such as business purpose and attendee details, before approving.',
      'Hold off on approving and flag the pattern first. The clustering just below the threshold is what matters, not whether individual receipts look fine.',
      'Run the standard duplicate check and process them if nothing flags automatically. The control environment is working as designed.',
    ],
    correctAnswer:
      'Hold off on approving and flag the pattern first. The clustering just below the threshold is what matters, not whether individual receipts look fine.',
    mossInsight: {
      body: 'This is threshold manipulation, one of the most common expense fraud techniques. Fraudsters learn the approval limits and intentionally stay just below them. The pattern across multiple submissions is the red flag, not any single receipt.',
      howMossHelps:
        'Moss tracks spending patterns over time, not just individual transactions. Repeated near-threshold submissions from the same person are visible in the data and simple to surface for review.',
    },
  },
  {
    id: 'sc-05-the-vendor-nobody-asked-for',
    type: 'multiple-choice',
    shortLabel: 'Phantom vendor fraud',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'While reviewing open payables, you find "NordTech Consulting UG" added to the system nine months ago by a senior finance employee. The same person approved all seven invoices from this vendor totaling EUR 63,000. The invoices reference "consulting services" with no deliverable attached. No contract on file. The registered address is a residential building. What is the appropriate action?',
    options: [
      'Request the missing contract and supporting documentation from the team member and put payments on hold until it arrives.',
      'Escalate to internal audit and legal, freeze further payments, and initiate a full review.',
      'Flag it for the next audit cycle and note it as a vendor documentation gap in the meantime.',
      'Introduce a mandatory dual-approval rule for this vendor going forward and request a formal contract before the next invoice is processed.',
    ],
    correctAnswer: 'Escalate to internal audit and legal, freeze further payments, and initiate a full review.',
    mossInsight: {
      body: 'Four red flags are stacking up here: one person creating and approving vendor payments, no contract, vague service descriptions, and a residential address. The separation of duties principle exists precisely to prevent this. Its absence is one of the clearest internal fraud signals in the book.',
      howMossHelps:
        "Moss enforces role separation by design. The person who creates a vendor cannot be the sole approver for that vendor's invoices. Every approval requires a second set of eyes.",
    },
  },
  {
    id: 'sc-06-urgent-wire-brand-new-account',
    type: 'multiple-choice',
    shortLabel: 'CFO voice spoofing',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      "You receive a call from what appears to be your CFO's mobile number. They say they are closing a time-sensitive acquisition deal and need an EUR 85,000 wire transfer within 45 minutes. The destination account is new and not in your system. Documentation will follow after the transfer. What do you do?",
    options: [
      'Process it. The request comes directly from the CFO and the situation is genuinely urgent.',
      'Ask the CFO to send the account details by email so you have written confirmation before processing.',
      "Put the transfer on hold and get your manager's sign-off before processing anything to a new account.",
      'Decline to process outside of the standard workflow and verify via an independent second channel before taking any action.',
    ],
    correctAnswer:
      'Decline to process outside of the standard workflow and verify via an independent second channel before taking any action.',
    mossInsight: {
      body: 'Phone numbers can be spoofed, and in 2024 scammers used AI voice cloning to successfully mimic CFO voices in real fraud cases. Urgency plus a new account plus no documentation is one of the most reliable fraud tripwires in B2B finance. Verifying through a second, independent channel is always the right call.',
      howMossHelps:
        'Payments to new or unregistered accounts in Moss cannot bypass the approval workflow even under time pressure. Every new payee requires verification before a transfer goes through.',
    },
  },
  {
    id: 'sc-07-too-good-to-pass-up',
    type: 'multiple-choice',
    shortLabel: 'Invoice redirection',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'A supplier you have worked with for two years sends an email offering a 12% early payment discount if you settle their outstanding EUR 8,500 invoice within 24 hours. The payment must go to a new bank account because their usual one is "temporarily suspended for system migration." What is your next move?',
    options: [
      'Process it. A 12% discount from a two-year supplier with a plausible explanation is worth acting on quickly.',
      'Reply to the email and ask for written confirmation of the account change.',
      'Pay to the original account this time and flag the new IBAN for verification before the next invoice.',
      'Call the supplier on the number already in your vendor master to confirm before changing any payment details.',
    ],
    correctAnswer:
      'Call the supplier on the number already in your vendor master to confirm before changing any payment details.',
    mossInsight: {
      body: 'Urgency plus a changed bank account is a textbook invoice redirection setup. It does not matter how attractive the discount looks or how legitimate the email appears. The only safe verification is a phone call you made yourself to a number you already have, not one found in the email.',
      howMossHelps:
        'Moss keeps invoices, vendors, and IBAN history in one structured system with a full audit trail. A new account for an old vendor does not just appear on the next wire; it lands as a change against what was there before, for someone to actually look at.',
    },
  },
  {
    id: 'sc-08-did-you-actually-read-that-name',
    type: 'multiple-choice',
    shortLabel: 'Lookalike vendor name',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'An invoice for EUR 4,200 lands in your AP inbox from "Microsofft Solutions GmbH" for "software license renewal." At first glance the vendor name looks slightly misspelled. Your company does use Microsoft products, the rest of the invoice looks polished, includes a German VAT number and an EU IBAN. You are about to process it because it looks routine. What should you do first?',
    options: [
      'Check the invoice number against your open purchase orders and approve if there is a match.',
      'Search the company name and VAT number online to confirm they belong to a legitimate business.',
      'Forward it to IT to check whether a license renewal is actually due before approving payment.',
      'Cross-check the vendor name, VAT number, and IBAN against your master data before taking any further action.',
    ],
    correctAnswer:
      'Cross-check the vendor name, VAT number, and IBAN against your master data before taking any further action.',
    mossInsight: {
      body: 'Lookalike vendor fraud works precisely because the amounts are believable and the invoices look professional. One transposed letter in a vendor name is all it takes. Master data verification before payment is the only reliable control. Googling a fake company name often returns nothing, which is not the same as confirming it is legitimate.',
      howMossHelps:
        'Moss flags invoices from unrecognized or new vendors and compares incoming details against existing master records. A lookalike vendor with a slightly different name is visible before a payment is ever scheduled.',
    },
  },
  {
    id: 'sc-09-the-salami-slicer',
    type: 'multiple-choice',
    shortLabel: 'Salami slicing scheme',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'During a routine review you notice your bookkeeper has been posting small rounding adjustments of EUR 0.01 to EUR 2.99 per transaction to a suspense account. This has occurred across roughly 3,000 transactions over 18 months. The bookkeeper explains it as "standard rounding for cleaner books." What is the correct response?',
    options: [
      'Accept the explanation. Individual amounts are immaterial and rounding differences are normal in bookkeeping.',
      'Run a spot check across 20 to 30 entries to assess whether the rounding pattern looks consistent and deliberate.',
      'Request a full reconciliation of the suspense account and escalate if any outflows cannot be explained.',
      'Ask the bookkeeper to reverse the open entries and stop the practice without opening a formal review.',
    ],
    correctAnswer:
      'Request a full reconciliation of the suspense account and escalate if any outflows cannot be explained.',
    mossInsight: {
      body: 'This is a classic salami slicing scheme, named because you steal in very thin slices that are individually meaningless. Across 3,000 transactions at even EUR 1.50 average, that is EUR 4,500. More importantly, the systematic nature of the pattern is itself the red flag, regardless of the total amount.',
      howMossHelps:
        'Moss records every card and invoice transaction with user, timestamp, and approval trail in one place. The fewer places postings live, the less room a salami scheme has to hide.',
    },
  },
  {
    id: 'sc-10-the-relationship-nobody-declared',
    type: 'multiple-choice',
    shortLabel: 'Procurement conflict',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      "You are reviewing company spend and notice that 71% of all IT procurement contracts in the past year went to one vendor, NordIT Solutions GmbH, with no competitive tender despite a policy requiring one above EUR 10,000. The vendor was registered 13 months ago. The procurement manager who signed every contract shares a surname with the company's registered owner. What is the appropriate course of action?",
    options: [
      'Escalate the vendor concentration to your CFO as a spend efficiency issue and recommend a competitive tender for the next contract cycle.',
      'Ask the procurement manager directly whether a personal relationship with the vendor exists.',
      'Escalate to internal audit or compliance, preserve all documentation, and request a formal conflict-of-interest review.',
      'Document the missing tender process as an audit finding and include it in the next scheduled procurement review.',
    ],
    correctAnswer:
      'Escalate to internal audit or compliance, preserve all documentation, and request a formal conflict-of-interest review.',
    mossInsight: {
      body: 'A single vendor taking 71 percent of spend with no competitive process and a possible undisclosed personal connection is not a process oversight. It is a conflict-of-interest red flag with potential corruption implications.',
      howMossHelps:
        'Moss gives finance leaders a transparent view of all spend concentration across vendors and time periods. Unusual supplier dominance in a category becomes visible before it accumulates into a serious compliance and financial exposure.',
    },
  },
  {
    id: 'sc-11-the-teams-call-that-was-not-real',
    type: 'multiple-choice',
    shortLabel: 'AI-generated video meeting',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'Your CFO adds you to an urgent Teams call with the CEO and outside counsel. They ask you to initiate a EUR 95,000 wire to an account they share in chat for a confidential vendor settlement. Faces are slightly pixellated and audio is slightly out of sync. What do you do?',
    options: [
      'Initiate the transfer using the account details in chat. Senior leadership plus legal counsel is sufficient authorization.',
      'Ask on the call for a confirming email from the CFO before initiating, and process once the email arrives.',
      'Leave the call, contact the CFO and CEO on a known-good channel, and refuse to initiate until each person confirms in person or via verified number.',
      'Ask the outside counsel to share the signed acquisition documents on the call and proceed once you have seen the cover page.',
    ],
    correctAnswer:
      'Leave the call, contact the CFO and CEO on a known-good channel, and refuse to initiate until each person confirms in person or via verified number.',
    mossInsight: {
      body: 'This is the Arup playbook, almost exactly. A Hong Kong finance employee sent around 25 million dollars in 2024 after a deepfake Teams call with a fake CFO and colleagues. Audio sync and pixellation are often subtle. The rule: break the channel before you authorize anything, and reverify with people you know.',
      howMossHelps:
        'Moss payment workflows do not accept out-of-band instructions. A transfer request begins inside the platform with a named, authenticated user and is approved through configured roles, regardless of what any call demanded.',
    },
  },
  {
    id: 'sc-12-quick-favor-a-stack-of-gift-cards',
    type: 'multiple-choice',
    shortLabel: 'CEO gift card request',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'You receive an email that looks like it is from your CEO, late Friday afternoon. It says they are in back-to-back client meetings and urgently need you to buy ten 200 euro Amazon gift cards for a client appreciation gesture, then email the redemption codes back. They say they will expense it Monday. What do you do?',
    options: [
      'Buy the cards from the company card and email the codes. The CEO is clear about the business purpose.',
      "Reply asking for the client's name and a purchase order number before proceeding.",
      'Do not act on the email. Contact the CEO on a known channel to confirm. This is a classic gift card scam pattern.',
      'Buy one card as a test and send the code first to see whether the CEO acknowledges it before buying the rest.',
    ],
    correctAnswer:
      'Do not act on the email. Contact the CEO on a known channel to confirm. This is a classic gift card scam pattern.',
    mossInsight: {
      body: 'Gift card requests from leadership over email are one of the most common BEC scams on the planet. Attackers love them because gift cards are essentially cash and nearly impossible to claw back once the code is sent. No legitimate CEO urgently needs gift cards by Friday. Ever.',
      howMossHelps:
        'Moss ties every reimbursement to an employee, a receipt, and an approval path. No one fronts 200 euros of personal money because a Friday email "from the CEO" said so.',
    },
  },
  {
    id: 'sc-13-the-9-990-euro-invoice',
    type: 'multiple-choice',
    shortLabel: 'Below-threshold structuring',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'Your approval threshold for single-signer sign-off is EUR 10,000. A new supplier has sent four consecutive invoices of EUR 9,990, EUR 9,850, EUR 9,970, and EUR 9,980 in a single week, all for general consulting. What is the most appropriate action?',
    options: [
      'Approve the four invoices individually because each falls under the single-signer threshold and they all have invoice numbers.',
      'Combine the invoices into a single approval request and route them together for second-signer review.',
      'Escalate the pattern. Treat it as potential invoice splitting intended to evade dual approval, and request the underlying contract and deliverables before paying any of them.',
      'Approve the first two invoices and hold the remaining two pending clarification from the supplier about why the work was billed separately.',
    ],
    correctAnswer:
      'Escalate the pattern. Treat it as potential invoice splitting intended to evade dual approval, and request the underlying contract and deliverables before paying any of them.',
    mossInsight: {
      body: 'This is invoice splitting, the supplier (or whoever inside colluded to onboard them) is deliberately sizing every invoice under the single-signer threshold. Each one might be legitimate, but the pattern is not. Controls that exist at a threshold stop working if anyone can choose whether to trigger them.',
      howMossHelps:
        'Moss allows to look at spending across invoices, suppliers, and time periods, not just at a single document. Clusters of invoices from one vendor sized just under approval thresholds show up as a pattern, not as normal activity.',
    },
  },
  {
    id: 'sc-14-a-thank-you-that-feels-heavy',
    type: 'multiple-choice',
    shortLabel: 'Undisclosed gift',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'Your procurement manager mentions that a key vendor sent them a personal thank-you: a weekend spa stay for two. The vendor is in the middle of contract negotiations that your manager is leading. There is no gift declaration in your compliance system. What is the right course of action?',
    options: [
      'Advise the manager to enjoy the gift but disclose it informally to HR to be safe, and continue the negotiation.',
      'Ask the manager to return the gift and document the return to preserve neutrality in the negotiation.',
      "Escalate to compliance or internal audit, pause the manager's involvement in the contract negotiation, and open a formal conflict-of-interest review.",
      "Document the gift in next year's code-of-conduct training and continue with negotiations since no decision has been made yet.",
    ],
    correctAnswer:
      "Escalate to compliance or internal audit, pause the manager's involvement in the contract negotiation, and open a formal conflict-of-interest review.",
    mossInsight: {
      body: 'A personal benefit from a vendor in the middle of negotiations is the textbook setup for a kickback. The right move is not managing the optics, it is removing the conflict and examining it properly.',
      howMossHelps:
        'Moss makes vendor spend patterns visible across the organization so unusual awards, persistent single-source contracting, or anomalous pricing do not disappear into a functional silo.',
    },
  },
  {
    id: 'sc-15-contractor-hours-that-never-drop',
    type: 'multiple-choice',
    shortLabel: 'Timesheet inflation',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'An offshore contractor has submitted timesheets for the past four months showing exactly 168 hours every month (42 hours per week flat). Invoices reference generic support tasks. There are no meeting records, deliverables, or project artifacts tied to those hours. The contractor reports to a manager who approves all timesheets without review. What is the correct first step?',
    options: [
      'Accept the timesheets. The contractor is within their agreed-upon weekly hours and the manager has approved.',
      'Ask the manager to double-check the timesheets and continue paying in the meantime to avoid straining the relationship.',
      'Hold further payments pending a review, request evidence of work, and assess whether separation of duties between timesheet entry and approval has broken down.',
      'Change the contract to a fixed-fee model so future hours are not the issue, and move on.',
    ],
    correctAnswer:
      'Hold further payments pending a review, request evidence of work, and assess whether separation of duties between timesheet entry and approval has broken down.',
    mossInsight: {
      body: "Flat hours, generic tasks, no artifacts, single approver who never questions. Four red flags of contractor fraud or ghost-hours billing. The fix is twofold: evidence of work for past hours, and restored separation of duties so a single manager cannot both generate and approve someone else's pay.",
      howMossHelps:
        'Moss ties contractor and team spend to named cost centers and approval chains with visibility for finance leadership, so one manager cannot run an opaque stream of spend unsupervised.',
    },
  },
  {
    id: 'sc-16-the-subscription-nobody-remembers',
    type: 'multiple-choice',
    shortLabel: 'The Subscription Nobody Remembers',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      "You spot a recurring EUR 85 per month SaaS charge on a team lead's card for a tool no one on the team still uses. The employee who signed up for it left nine months ago. What do you do?",
    options: [
      'Keep it running and ask the team lead to find a new internal owner.',
      'Cancel the subscription going forward and add the tool to the SaaS review.',
      'Park it until the annual budget cleanup and downgrade to the cheapest tier in case there is still some business use.',
      "Move the charge to a shared admin cost center so the team's budget looks clean.",
    ],
    correctAnswer: 'Cancel the subscription going forward and add the tool to the SaaS review.',
    mossInsight: {
      body: 'Ghost SaaS is one of the quietest drains on any finance team. When a subscription outlives the employee who set it up, the cost just keeps compounding. Every recurring charge needs an owner, a review cycle, and a clear cancellation path.',
      howMossHelps:
        'Moss shows every recurring card charge and the employee it is tied to. When someone leaves, their subscriptions surface as orphaned spend rather than disappearing into the statement.',
    },
  },
  {
    id: 'sc-17-bank-system-migration-they-say',
    type: 'multiple-choice',
    shortLabel: 'Spoofed internal domain',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'You receive an internal-looking email from it-infrastructure@yourcompany-de.com (your actual domain ends in .com, not .com-de.com). It says the corporate bank is migrating systems this weekend and all recurring supplier transfers should be temporarily routed through a new intermediary IBAN listed in the email. The language and branding look internal. What do you do?',
    options: [
      'Update the recurring payment destinations for the affected suppliers and confirm back to the IT-infrastructure address so they can track the migration.',
      'Reply to the sender to ask whether this is genuinely from IT and wait for their confirmation before making any change.',
      'Do not act. Verify directly with IT and Treasury on known channels. Report the email as a phishing attempt and preserve the original for the security team.',
      'Apply the change for one small supplier first as a test and escalate only if the test payment fails.',
    ],
    correctAnswer:
      'Do not act. Verify directly with IT and Treasury on known channels. Report the email as a phishing attempt and preserve the original for the security team.',
    mossInsight: {
      body: "Spoofed internal-looking domains are a staple of BEC. The attacker relies on the branding, the technical-sounding narrative, and the target's urge to comply with what sounds like IT. Bank migrations are a common cover story. The only safe response is to break the channel and verify directly.",
      howMossHelps:
        'In Moss, supplier IBANs sit in vendor master data and every change is logged. A bank migration email cannot silently reroute payments; any IBAN change requires an explicit in platform update and approval.',
    },
  },
  {
    id: 'sc-18-disaster-relief-perfect-timing',
    type: 'multiple-choice',
    shortLabel: 'Fake charity appeal',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      "Three days after a major earthquake makes global headlines, your company receives an urgent email from what appears to be an international aid foundation, asking for an immediate EUR 2,000 corporate donation before the fundraising window closes. The email includes an IBAN in a different country from the charity's headquarters and an emotive appeal with generic photos. What is the correct response?",
    options: [
      'Donate the full EUR 2,000 to join the early wave of relief and file the tax paperwork afterwards.',
      'Ask the sender for their charity registration and a scan of the certificate, then decide based on what they send.',
      'Verify the charity yourself in the national register and approve the donation if the registration checks out.',
      'Flag the red flags, forward to the CSR or leadership team with your notes, and let the decision go through the proper giving process.',
    ],
    correctAnswer:
      'Flag the red flags, forward to the CSR or leadership team with your notes, and let the decision go through the proper giving process.',
    mossInsight: {
      body: "Disaster-themed charity fraud spikes within hours of any major headline, using emotional pressure and recycled imagery to push a fast decision. The test is simple: can you confirm the charity's registration and bank details on a channel you already trust? If not, you do not pay, no matter how urgent the appeal feels.",
      howMossHelps:
        'Moss captures counterparty data on every outgoing transfer, so even charitable donations go through verified destinations and documented approval rather than ad-hoc decisions.',
    },
  },
  {
    id: 'sc-19-pay-us-in-bitcoin-please',
    type: 'multiple-choice',
    shortLabel: 'Supplier crypto request',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      "A long-standing German supplier unexpectedly asks that next month's EUR 38,000 invoice be settled in Bitcoin to a wallet address they provide. They explain it is easier for their accounting. You have always paid them in euros by SEPA before. What should you do?",
    options: [
      'Arrange a one time crypto purchase and require SEPA again from next cycle.',
      'Reply asking for written confirmation of the change on letterhead, then decide.',
      'Pay via the SEPA account on file this cycle and call the supplier on a known number to verify the crypto request.',
      'Escalate to the CFO and hold until the supplier submits a formal payment method change from their finance team.',
    ],
    correctAnswer:
      'Pay via the SEPA account on file this cycle and call the supplier on a known number to verify the crypto request.',
    mossInsight: {
      body: 'An established supplier suddenly requesting crypto for an otherwise normal invoice is a very strong fraud signal. Either their systems are compromised or someone is impersonating them. Bitcoin payments are irreversible and nearly impossible to recover. The correct move is to fall back to the verified channel and reverify the request independently.',
      howMossHelps:
        'Moss routes approved supplier payments through regulated rails with verified counterparties. A wallet address in an email cannot replace a stored, verified IBAN.',
    },
  },
  {
    id: 'sc-20-the-qr-code-on-the-parking-ticket',
    type: 'multiple-choice',
    shortLabel: 'Malicious QR code',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'A colleague submits an expense for a parking fine paid via a QR code on the ticket. The payment URL behind the QR code uses a misspelled domain. What do you do?',
    options: [
      'Reimburse and remind the colleague to be careful with QR codes next time.',
      'Ask the colleague to repay the fine through the official municipal portal and release the reimbursement only once confirmed.',
      'Pause the reimbursement, loop in IT security, freeze the card, and rotate credentials. Treat it as quishing.',
      'Reimburse based on the paper ticket since the fine is real, and flag the QR finding to IT as a side note.',
    ],
    correctAnswer:
      'Pause the reimbursement, loop in IT security, freeze the card, and rotate credentials. Treat it as quishing.',
    mossInsight: {
      body: 'Fake parking ticket QR codes are an active quishing technique. The QR on the paper ticket leads to a lookalike payment portal that captures card details. The colleague may already have compromised card data, and IT security needs to move quickly. The receipt authenticity question is secondary.',
      howMossHelps:
        'Moss monitors card activity in real time. If the colleague used a Moss card and it gets misused later, card-level limits and instant freeze stop the exposure at the card, not the account.',
    },
  },
  {
    id: 'sc-21-no-purchase-order-just-trust-me',
    type: 'multiple-choice',
    shortLabel: 'Missing PO',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'A department head asks AP to process an EUR 18,000 invoice from a new marketing agency. There is no purchase order in the system and no contract attached. The department head says it was an emergency engagement and asks to sort the paperwork later. What is the correct response?',
    options: [
      'Process and open a retroactive PO under the urgent engagement grace period.',
      'Process as a one off journal entry and have the department head initiate the PO this week.',
      'Split into two EUR 9,000 payments, pay the first now and the second once the contract is in place.',
      'Hold the invoice, require a signed contract and PO before processing, escalate if the department head pushes back.',
    ],
    correctAnswer:
      'Hold the invoice, require a signed contract and PO before processing, escalate if the department head pushes back.',
    mossInsight: {
      body: 'Paperwork later almost always means paperwork never. A PO is not a finance ritual, it is the one piece of evidence that someone, before the money moved, said "yes, go do this." After payment, the vendor and the requester get to write the story however they want.',
      howMossHelps:
        'Moss supports PO-based workflows and invoice matching, so an invoice without a matching PO never slips quietly into a payment run.',
    },
  },
  {
    id: 'sc-22-please-update-my-own-salary-account',
    type: 'multiple-choice',
    shortLabel: 'Self-serve bank change',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'An employee emails HR asking to update their own salary-receiving bank account to a new IBAN. The email comes from their regular work address and includes a screenshot of the supposed new account statement. Payroll runs in four days. What should HR do?',
    options: [
      'Update the IBAN since the email came from the verified work address and includes a screenshot.',
      'Update, send a EUR 1 pre note, and confirm with the employee before the full salary run.',
      'Pause and verify out of band (phone call, in person, authenticated self service). Only update once identity is confirmed.',
      'Accept for this cycle and ask the employee to formalize it in HR self service within 14 days.',
    ],
    correctAnswer:
      'Pause and verify out of band (phone call, in person, authenticated self service). Only update once identity is confirmed.',
    mossInsight: {
      body: 'A compromised employee inbox is one of the easiest ways to redirect salary payments. The email looks legitimate because it comes from the real address. The fraud signal is not the content, it is the channel. Any change to a payout IBAN must be verified out-of-band before it takes effect.',
      howMossHelps:
        "Card access and reimbursement payouts in Moss are tied to each employee's authenticated profile, not to an email. A compromised inbox cannot quietly reroute money inside the platform.",
    },
  },
  {
    id: 'sc-23-the-finanzamt-wants-its-money',
    type: 'multiple-choice',
    shortLabel: 'Fake tax notice',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'Your general mailbox receives a letter branded as the local Finanzamt (tax office), stating that an unpaid EUR 12,500 corporate tax liability must be wired to a Luxembourg IBAN within 72 hours to avoid enforcement. The letter uses official-looking seals and a reference number. What is the correct first action?',
    options: [
      'Pay the amount promptly to avoid enforcement action and contest it later through your tax advisor.',
      'Call the phone number printed on the letter to confirm, then pay if the person confirms the amount.',
      'Do not pay. Contact your tax advisor and the Finanzamt directly using contact details you already have on file, and verify whether the notice is genuine.',
      'Reply to the letter asking for a copy of the original tax assessment and pay once it arrives.',
    ],
    correctAnswer:
      'Do not pay. Contact your tax advisor and the Finanzamt directly using contact details you already have on file, and verify whether the notice is genuine.',
    mossInsight: {
      body: 'Tax authority impersonation is a popular European fraud theme, with fraudsters spoofing Finanzamt, HMRC, or national tax authorities. Real tax agencies never ask for payment to foreign IBANs, and enforcement procedures follow formal documented steps that arrive well in advance of any payment deadline. Verify on a channel you control, not one printed on the letter.',
      howMossHelps:
        'Moss routes outgoing payments through structured approvals with visibility of counterparties and destination countries, so a Luxembourg IBAN for a German tax liability would trigger review, not processing.',
    },
  },
  {
    id: 'sc-24-wrong-account-on-purpose',
    type: 'multiple-choice',
    shortLabel: 'Refund to new account',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'A customer requests a EUR 4,900 refund for a cancelled order. The original payment was made by SEPA transfer from one account. The customer asks for the refund to be sent to a different IBAN in another country, explaining that their original bank account is temporarily frozen. What do you do?',
    options: [
      "Refund to the new IBAN based on the customer's explanation, documenting the freeze reason.",
      'Refund to the original payment account in line with standard refund policy, and revisit the destination through a separate verified process if needed.',
      'Split the refund across the original and the new IBAN to balance experience and risk.',
      'Hold until the customer provides a bank letter confirming the freeze, then release to the new IBAN.',
    ],
    correctAnswer:
      'Refund to the original payment account in line with standard refund policy, and revisit the destination through a separate verified process if needed.',
    mossInsight: {
      body: 'Refunds should be sent back to the originating account. Redirecting a refund to a different IBAN is both a money-laundering signal and a fraud vector (stolen cards used to purchase, legitimate IBAN used to collect the refund). Follow the money back the way it came in unless a strict, verified exception process applies.',
      howMossHelps:
        'Counterparty details in Moss sit in vendor records. Changing a payment destination is not a one off instruction, it is a logged update that needs explicit approval.',
    },
  },
  {
    id: 'sc-25-an-anonymous-note-from-an-insider',
    type: 'multiple-choice',
    shortLabel: 'Anonymous report',
    phase: 1,
    questionGroup: 'p1-scenario-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'Your finance team receives an anonymous tip via your EU whistleblower channel alleging that a senior colleague is routing supplier invoices through a shell company. The tip includes specific invoice numbers and a bank IBAN. What is the correct handling?',
    options: [
      'Confront the named colleague directly to hear their side and resolve the matter quickly.',
      'Dismiss the tip as uncorroborated since it was anonymous and focus on the next audit cycle.',
      'Treat it as a protected whistleblower disclosure, route to compliance, preserve confidentiality, and open an independent review of the named transactions.',
      "Share the tip with the named colleague's manager so they can investigate informally within the team.",
    ],
    correctAnswer:
      'Treat it as a protected whistleblower disclosure, route to compliance, preserve confidentiality, and open an independent review of the named transactions.',
    mossInsight: {
      body: 'Tip offs catch more corporate fraud than audits, analytics, and management review put together. Almost half of all fraud comes to light because someone on the inside decided to say something. Handle tips well and you have the best fraud detection tool your company owns. Handle them badly and nobody speaks up again.',
      howMossHelps:
        'Moss provides the transactional evidence needed to validate or dismiss such allegations quickly. Invoice history, vendor bank details, approval trails, and user actions are all searchable in one place.',
    },
  },
  {
    id: 'kb-01-where-do-the-biggest-losses-really-come-from',
    type: 'multiple-choice',
    shortLabel: 'BEC email fraud',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'Which fraud type causes the highest financial losses in business payments, according to industry reports?',
    options: [
      'Card-present fraud at point of sale',
      'Business Email Compromise (BEC): invoice and payment fraud via email impersonation',
      'Duplicate invoice schemes',
      'Payroll fraud',
    ],
    correctAnswer: 'Business Email Compromise (BEC): invoice and payment fraud via email impersonation',
    mossInsight: {
      body: 'No malware. No hacking. Just a convincing email and one person under pressure. BEC is one of the most damaging fraud categories for finance and AP teams because it goes straight for the payment workflow. The most expensive cyberattack tool is still just email.',
      howMossHelps:
        'Moss makes IBAN changes, payment details, and approval flows fully visible and traceable. High-risk transactions cannot bypass the workflow, so there is always a second set of eyes before money moves.',
    },
  },
  {
    id: 'kb-02-prevent-it-or-react-to-it',
    type: 'multiple-choice',
    shortLabel: 'Preventive controls',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'Which of the following is widely considered a best-practice control for preventing budget overruns in decentralized teams?"',
    options: [
      'Reviewing expenses monthly and adjusting budgets after the fact',
      'Providing budget visibility primarily at CFO level',
      'Enabling real-time spend visibility with predefined approval workflows',
      'Running quarterly financial reviews with department heads',
    ],
    correctAnswer: 'Enabling real-time spend visibility with predefined approval workflows',
    mossInsight: {
      body: 'By the time a budget overrun shows up in your monthly review, the money is already gone. Preventive controls stop the problem before it happens. Detective controls just give you a nicer way to explain it afterward. The difference is not subtle.',
      howMossHelps:
        'Moss gives teams real-time budget visibility with card limits and approval workflows built in. Spending stays within budget before it is committed, not after it shows up in a monthly review.',
    },
  },
  {
    id: 'kb-03-the-real-estate-laundry',
    type: 'multiple-choice',
    shortLabel: 'Money laundering stages',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'Illicit cash is used to buy property. Two years later the property is sold and the proceeds are declared as legitimate profit. What just happened?',
    options: [
      'Tax optimization through asset structuring',
      'Integration of illicit funds into the legal economy',
      'Reclassification of funds as operational income',
      'standard long-term investment cycle',
    ],
    correctAnswer: 'Integration of illicit funds into the legal economy',
    mossInsight: {
      body: 'The money is no longer "dirty." On paper it looks like a normal investment return. That is the entire purpose of integration, the third and final stage of money laundering. The three stages are Placement (getting the money into the system), Layering (obscuring the trail), and Integration (making it look legitimate). Once here, tracing it is extremely difficult.',
      howMossHelps:
        'Moss brings card spend, invoices, and approvals into one platform with a full audit trail. Every transaction has a named owner, a clear approval path, and a timestamp, which makes unusual activity easier for finance teams to spot.',
    },
  },
  {
    id: 'kb-04-never-takes-a-day-off-employee-fraud',
    type: 'multiple-choice',
    shortLabel: 'Internal fraud signals',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'An employee never takes leave, refuses to share system access, and reacts defensively when questioned about vendor changes. No visible accounting discrepancies exist. What is the real concern?',
    options: [
      'An accounting inconsistency not yet reflected in the books',
      "Emerging cash flow risk in the team's processes",
      'Control risk and potential opportunity for internal fraud',
      'Possible tax misclassification or compliance exposure',
    ],
    correctAnswer: 'Control risk and potential opportunity for internal fraud',
    mossInsight: {
      body: 'The most reliable early warning signs often have nothing to do with the numbers. Refusing leave, avoiding access sharing, and reacting defensively to questions about vendors are well-documented precursors to internal fraud. These behavioral patterns appear long before any financial discrepancy shows up.',
      howMossHelps:
        'Moss enforces separation of duties, role-based access controls, and approval workflows. No single person can run a process end-to-end unsupervised, regardless of how trusted they are.',
    },
  },
  {
    id: 'kb-05-the-magic-three-in-ap',
    type: 'multiple-choice',
    shortLabel: '3-way match',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'In accounts payable, what does a "3-way match" verify before a supplier invoice is approved for payment?',
    options: [
      "The invoice amount, the payment date, and the supplier's tax number",
      'The purchase order, the goods receipt, and the supplier invoice',
      'The budget code, the cost center, and the department approval',
      'The IBAN, the invoice number, and the contract reference',
    ],
    correctAnswer: 'The purchase order, the goods receipt, and the supplier invoice',
    mossInsight: {
      body: 'The 3-way match is one of the oldest and most effective controls in AP. Before any invoice gets paid, three documents need to align: what you ordered, what you actually received, and what the supplier is charging. When all three match, you pay. When they do not, you investigate. Simple in theory, frequently skipped in practice.',
      howMossHelps:
        'Moss automates the matching of purchase orders and invoices, flagging any discrepancies before a payment ever reaches the approval stage.',
    },
  },
  {
    id: 'kb-06-what-is-smurfing',
    type: 'multiple-choice',
    shortLabel: 'Smurfing structuring',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'In anti-money laundering terminology, what does "smurfing" refer to?',
    options: [
      'Using shell companies across multiple jurisdictions to hide the origin of funds',
      'Breaking large sums of illicit money into smaller transactions to avoid reporting thresholds',
      'Layering funds through crypto exchanges to make tracing more difficult',
      'Falsifying payroll records to divert salary payments into personal accounts',
    ],
    correctAnswer: 'Breaking large sums of illicit money into smaller transactions to avoid reporting thresholds',
    mossInsight: {
      body: 'Smurfing is a form of structuring. Instead of depositing EUR 50,000 at once (which triggers automatic reporting in most jurisdictions), a money launderer splits it into many smaller deposits across different accounts or branches. The goal is to stay below the radar of automated detection systems. Classic placement-stage technique.',
      howMossHelps:
        'Moss shows card spend and invoices from the same counterparty in one place, with approval thresholds applied consistently across transactions. Clusters of near threshold payments or repeated structuring sit side by side in the data rather than hidden across separate approval flows.',
    },
  },
  {
    id: 'kb-07-the-one-rule-that-prevents-most-fraud',
    type: 'multiple-choice',
    shortLabel: 'Separation of duties',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'Which internal control principle most directly reduces the risk of a single employee committing and concealing financial fraud?',
    options: [
      'Mandatory dual authorization for all payments above a defined threshold',
      'Separation of duties across transaction initiation, approval, and recording',
      'Regular reconciliation of all bank accounts and payment ledgers',
      'Conducting surprise audits on a random selection of transactions each quarter.',
    ],
    correctAnswer: 'Separation of duties across transaction initiation, approval, and recording',
    mossInsight: {
      body: 'Separation of duties means no single person should initiate a payment, approve it, and record it in the books. Split those three steps across different people and fraud requires active collusion between multiple parties, which is significantly harder to coordinate and sustain over time.',
      howMossHelps:
        'Moss enforces separation of duties through configurable approval flows. The person requesting a payment is never the only one authorizing it.',
    },
  },
  {
    id: 'kb-08-kyc-what-is-it-actually-for',
    type: 'multiple-choice',
    shortLabel: 'KYC compliance',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'What is the primary purpose of KYC (Know Your Customer) processes in financial services?',
    options: [
      'To improve product recommendations based on customer transaction history',
      'To verify the identity of clients and assess the risk of illegal activity such as money laundering or fraud',
      'To ensure customers receive the correct pricing tier based on transaction volume',
      'To track customer satisfaction and net promoter scores',
    ],
    correctAnswer:
      'To verify the identity of clients and assess the risk of illegal activity such as money laundering or fraud',
    mossInsight: {
      body: 'KYC is not a compliance formality. It is the first real gate against financial crime. By verifying who a customer is before any money moves, KYC prevents bad actors from entering financial systems in the first place. EU regulators have significantly tightened KYC requirements under the 2024 AML reform package.',
      howMossHelps:
        'Moss itself is a regulated fintech, so onboarding a company onto Moss goes through the same KYC checks banks run.',
    },
  },
  {
    id: 'kb-09-where-does-procurement-fraud-actually-start',
    type: 'multiple-choice',
    shortLabel: 'Vendor onboarding risk',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'According to fraud research, which phase of the procurement cycle is most vulnerable to fraud?',
    options: [
      'Contract execution and delivery monitoring',
      'Vendor selection and onboarding',
      'Invoice receipt and payment processing',
      'Budget planning and allocation',
    ],
    correctAnswer: 'Vendor selection and onboarding',
    mossInsight: {
      body: 'Vendor selection is where the highest-value, longest-running fraud gets embedded. Fake vendors, kickback schemes, and conflicts of interest are set up at the onboarding stage, often years before anyone notices. Once a fraudulent vendor is in the master data, every subsequent invoice looks completely legitimate.',
      howMossHelps: 'Moss keeps vendor payment details and approvals in one platform with a full audit trail.',
    },
  },
  {
    id: 'kb-10-the-three-sides-of-every-fraud',
    type: 'multiple-choice',
    shortLabel: "Cressey's model",
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'According to the classic Fraud Triangle model, which three elements are typically present when an employee commits occupational fraud?',
    options: [
      'Motive, Means, and Opportunity',
      'Pressure, Opportunity, and Rationalization',
      'Access, Authority, and Anonymity',
      'Stress, Greed, and Control Weakness',
    ],
    correctAnswer: 'Pressure, Opportunity, and Rationalization',
    mossInsight: {
      body: "Donald Cressey's 1953 model remains the bedrock of modern fraud theory. Pressure (financial or personal strain), Opportunity (a weakness in controls), and Rationalization (I will pay it back, they owe me this) are the three conditions almost always present. Take away one side of the triangle and most fraud never happens.",
      howMossHelps:
        'Moss removes Opportunity from the triangle by design: role-based access, structured approval flows, and complete visibility turn the gap fraudsters rely on into a locked door.',
    },
  },
  {
    id: 'kb-11-moving-money-to-hide-money',
    type: 'multiple-choice',
    shortLabel: 'Layering stage',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'Which stage of money laundering involves rapidly moving funds through multiple accounts, currencies, or jurisdictions to obscure their origin?',
    options: ['Placement', 'Layering', 'Integration', 'Reconciliation'],
    correctAnswer: 'Layering',
    mossInsight: {
      body: 'Layering is the middle and most complex stage of laundering. Once illicit cash is in the system (Placement), it gets bounced through shell accounts, wire transfers, crypto swaps, and cross-border hops to break any clean audit trail. When the money finally surfaces as a real estate purchase or investment return, that is Integration.',
      howMossHelps:
        'Moss logs every hop of every payment with verified counterparties, timestamps, and approvals. The kind of opaque movement that enables layering simply does not exist in a Moss environment.',
    },
  },
  {
    id: 'kb-12-two-pairs-of-eyes-every-time',
    type: 'multiple-choice',
    shortLabel: 'Dual approval control',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      'Which control requires that every sensitive transaction is initiated by one person and independently verified or approved by another before it takes effect?',
    options: ['Segregation of assets', 'Maker-checker', 'Reasonableness review', 'Capital adequacy test'],
    correctAnswer: 'Maker-checker',
    mossInsight: {
      body: 'Maker-checker is the operational sibling of separation of duties. One person prepares, another authorizes. Banks have used it for a hundred years. It is now standard in payment systems, software releases, and any workflow where a single mistake or single bad actor could cause material harm.',
      howMossHelps:
        'Moss applies maker-checker logic to payments, vendor changes, and card issuance, so nothing material happens with only one person in the loop.',
    },
  },
  {
    id: 'kb-13-a-number-fraudsters-always-underweight',
    type: 'multiple-choice',
    shortLabel: 'Leading digit distribution',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      "According to Benford's Law, which digit appears as the leading digit of naturally occurring financial data most often?",
    options: [
      '1 (around 30% of the time)',
      '5 (around 20% of the time)',
      '9 (around 15% of the time)',
      'Each digit 1 through 9 appears roughly 11% of the time',
    ],
    correctAnswer: '1 (around 30% of the time)',
    mossInsight: {
      body: "Benford's Law predicts that the digit 1 leads about 30.1 percent of numbers in organic financial data, 2 leads about 17.6 percent, and so on, down to 9 at just 4.6 percent. Forensic auditors use this to flag manipulated datasets. When fabricated amounts are too evenly distributed (or over-weighted in the 7 to 9 range), it is a strong signal to look harder.",
      howMossHelps:
        'With all spend centralized in Moss, data-driven analysis including distribution checks across vendors, amounts, and time periods becomes straightforward rather than a one-off project.',
    },
  },
  {
    id: 'kb-14-the-paper-trail-that-defends-you',
    type: 'multiple-choice',
    shortLabel: 'Audit trail definition',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'In the context of financial controls, what does an audit trail refer to?',
    options: [
      'chronological record of transactions and related approvals, sufficient to reconstruct events end to end',
      'scheduled series of annual external audits across business units',
      "marker placed in the ERP system for each quarter's closing balance",
      'list of exceptions identified during the month-end reconciliation',
    ],
    correctAnswer:
      'chronological record of transactions and related approvals, sufficient to reconstruct events end to end',
    mossInsight: {
      body: 'An audit trail is the sequence of evidence that lets you reconstruct exactly who did what, when, and why. Without it, both fraud investigations and regulatory audits stall immediately. Modern systems generate audit trails automatically, which is why manual spreadsheet-and-email workflows remain so risky by comparison.',
      howMossHelps:
        'Moss generates a complete, tamper-evident audit trail across every transaction, approval, and vendor change, accessible in seconds rather than assembled across mailboxes and drives.',
    },
  },
  {
    id: 'kb-15-card-data-rules-you-have-to-follow',
    type: 'multiple-choice',
    shortLabel: 'PCI DSS scope',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'Who must comply with the Payment Card Industry Data Security Standard (PCI DSS)?',
    options: [
      'Only the card networks (Visa, Mastercard, Amex)',
      'Only banks that issue credit cards',
      'Any organization that stores, processes, or transmits cardholder data',
      'Only merchants processing more than USD 1 million in annual card volume',
    ],
    correctAnswer: 'Any organization that stores, processes, or transmits cardholder data',
    mossInsight: {
      body: 'PCI DSS is the unwritten rulebook of anyone handling card data. No law, no court, just the card networks pulling strings behind the scenes. If card data so much as brushes your systems, you are in the game, and the 2025 update means "we outsourced it" no longer gets you a free pass.',
      howMossHelps:
        'Moss uses tokenized virtual and physical cards issued by regulated providers, so sensitive card data does not sit on your systems to protect in the first place.',
    },
  },
  {
    id: 'kb-16-the-report-banks-must-file',
    type: 'multiple-choice',
    shortLabel: 'Suspicious Activity Report',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'In the EU and US, what does the term SAR (or STR) refer to in financial crime?',
    options: [
      'Supplier Authorization Record used in accounts payable',
      'Suspicious Activity Report filed with authorities when a transaction looks linked to financial crime.',
      'Structured Asset Register maintained for audit purposes',
      'Settlement Activity Report reconciling card transactions',
    ],
    correctAnswer:
      'Suspicious Activity Report filed with authorities when a transaction looks linked to financial crime.',
    mossInsight: {
      body: 'SAR (in the US) or STR (Suspicious Transaction Report, in many EU jurisdictions) is the formal filing regulated entities must submit when they see activity that does not add up. Failure to file when required is itself a regulatory offense.',
      howMossHelps:
        'Moss fulfills its own reporting obligations and uses transaction monitoring to surface potentially suspicious activity, keeping customers safely on the right side of compliance.',
    },
  },
  {
    id: 'kb-17-the-business-version-of-kyc',
    type: 'multiple-choice',
    shortLabel: 'Know Your Business',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'What is KYB (Know Your Business) in financial compliance?',
    options: [
      "marketing framework for understanding corporate customers' preferences",
      "due diligence process to verify a business entity's identity, ownership, and risk profile before onboarding it.",
      'benchmarking methodology for comparing business KPIs',
      'tax optimization review conducted annually for B2B clients',
    ],
    correctAnswer:
      "due diligence process to verify a business entity's identity, ownership, and risk profile before onboarding it.",
    mossInsight: {
      body: 'KYB is how regulated providers look past the business name on the paperwork and check who actually owns and controls the company. Legal existence, ultimate beneficial owners, sanctions exposure, industry, risk profile. It is one of the main defenses against shell company laundering,',
      howMossHelps:
        'Moss performs KYC at onboarding, verifying business registration, ownership, and risk signals before any spend infrastructure is issued.',
    },
  },
  {
    id: 'kb-18-checking-the-blacklist-before-you-pay',
    type: 'multiple-choice',
    shortLabel: 'Sanctions list checks',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'What does sanctions screening in payments compliance involve?',
    options: [
      'Reviewing supplier performance against a blacklist of underperforming vendors',
      'Running customers, counterparties, and transactions against government sanctions lists to block or flag prohibited dealings',
      'Checking whether a supplier has been blacklisted by a competitor',
      'Screening employees for past disciplinary records before onboarding',
    ],
    correctAnswer:
      'Running customers, counterparties, and transactions against government sanctions lists to block or flag prohibited dealings',
    mossInsight: {
      body: 'Sanctions screening is how payment providers and banks stop money flowing to designated individuals, entities, regimes, or regions. Lists come from the EU, OFAC (US Treasury), UN, and national authorities. A missed screening hit can mean criminal penalties, which is why banks now screen both at onboarding and on every transaction in real time.',
      howMossHelps:
        'Outgoing Moss payments run on regulated banking rails that carry sanctions screening at the transaction level.',
    },
  },
  {
    id: 'kb-19-the-payment-that-comes-back-to-you',
    type: 'multiple-choice',
    shortLabel: 'Corruption / kickback',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'In occupational fraud, what is a kickback scheme?',
    options: [
      'refund mechanism where overpayments are returned to the buyer',
      'corruption scheme in which a vendor secretly returns a portion of the contract price to the employee who awarded the contract',
      'supplier discount conditional on long-term purchasing commitments',
      'An internal process where unused budget is returned to the CFO at year end',
    ],
    correctAnswer:
      'corruption scheme in which a vendor secretly returns a portion of the contract price to the employee who awarded the contract',
    mossInsight: {
      body: 'Kickbacks are a classic form of corruption. The vendor inflates their price or wins a contract they should not have, and a slice of the money flows back to the insider who steered the deal.',
      howMossHelps:
        'Moss ties vendor setup, invoices, and payments to role based approval flows with a full audit trail, so one insider cannot quietly steer contracts to a favored supplier without a second set of eyes.',
    },
  },
  {
    id: 'kb-20-protection-built-into-eu-law',
    type: 'multiple-choice',
    shortLabel: 'EU Whistleblower Directive',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'The EU Whistleblower Directive (2019/1937) primarily does what?',
    options: [
      'Requires all EU companies to publish an annual fraud report',
      'Requires member states to ensure secure reporting channels and prohibits retaliation against people who report breaches of EU law in a work context',
      'Obligates the European Commission to run a centralized whistleblower hotline',
      'Removes confidentiality protections for all fraud whistleblowers',
    ],
    correctAnswer:
      'Requires member states to ensure secure reporting channels and prohibits retaliation against people who report breaches of EU law in a work context',
    mossInsight: {
      body: 'The Directive came into force to protect the people who report corruption, fraud, and other breaches of EU law from retaliation. Companies with 50+ employees must provide secure internal reporting channels. Strong whistleblower protection is one of the most effective fraud-deterrence measures there is: tip-offs account for 43 percent of all fraud detections globally.',
      howMossHelps:
        'Moss gives compliance a clean audit trail of transactions, approvals, and users, so investigating a tip internally can happen quietly through the data instead of pulling in the whole team.',
    },
  },
  {
    id: 'kb-21-why-your-bank-asks-twice',
    type: 'multiple-choice',
    shortLabel: 'PSD2 SCA',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      "The EU's Payment Services Directive 2 (PSD2) requires Strong Customer Authentication (SCA) for most online payments and account access. What does SCA actually require?",
    options: [
      'A single strong password for each online banking session.',
      'At least two of three factors: something you know, something you have, and something you are.',
      'Biometric authentication only for high value transactions.',
      "An annual identity re verification by the customer's bank.",
    ],
    correctAnswer: 'At least two of three factors: something you know, something you have, and something you are.',
    mossInsight: {
      body: "SCA is the EU's answer to account-takeover and card fraud: combine a password (know), a device or hardware token (have), or a fingerprint or face scan (are). At least two. It is why online card payments in Europe frequently trigger a phone-based confirmation. EBA data shows fraud rates are about five times higher on transactions authenticated without SCA compared to those that are authenticated with it.",
      howMossHelps:
        'In Moss logging in or approving sensitive actions needs more than one factor, which is what makes account takeover so much harder.',
    },
  },
  {
    id: 'kb-22-the-banking-law-about-being-hacked',
    type: 'multiple-choice',
    shortLabel: 'DORA scope',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'The EU Digital Operational Resilience Act (DORA), applicable since January 2025, primarily does what?',
    options: [
      "Regulates only banks' cybersecurity obligations inside the EU",
      'Sets uniform EU rules for ICT risk, incident reporting, and third party risk across most financial entities.',
      'Replaces GDPR for the financial sector',
      'Applies only to critical infrastructure outside the financial sector',
    ],
    correctAnswer:
      'Sets uniform EU rules for ICT risk, incident reporting, and third party risk across most financial entities.',
    mossInsight: {
      body: 'DORA is the EU telling finance that "our cloud was down" is no longer an excuse. Shared rules on cyber risk, outage reporting, and vendor oversight now cover banks, insurers, fintechs, and the critical tech providers behind them. Major incidents get reported to supervisors on tight timelines, no quiet fixing over the weekend.',
      howMossHelps:
        "As a regulated EU fintech, Moss sits inside DORA's scope, so incident response, ICT risk, and third party oversight are baked into how the platform is run.",
    },
  },
  {
    id: 'kb-23-the-rulebook-for-eu-anti-money-laundering',
    type: 'multiple-choice',
    shortLabel: 'AML Package',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      "What is the main change introduced by the EU's 2024 Anti-Money Laundering package compared with earlier directives?",
    options: [
      'It weakens reporting obligations for banks below a certain size',
      'It introduces a single directly applicable Regulation (AMLR) and creates a new EU-wide Anti-Money Laundering Authority (AMLA) with supervisory powers',
      'It replaces national financial intelligence units with a single EU FIU',
      'It removes crypto-asset service providers from AML scope',
    ],
    correctAnswer:
      'It introduces a single directly applicable Regulation (AMLR) and creates a new EU-wide Anti-Money Laundering Authority (AMLA) with supervisory powers',
    mossInsight: {
      body: 'The 2024 AML package reduces national divergence by introducing the directly applicable AMLR and establishing AMLA with direct supervision of selected high-risk cross-border entities, while coordinating national supervisors. It materially addresses fragmentation, though outcomes will depend on implementation and supervisory capacity.',
      howMossHelps:
        'Moss is a BaFin licensed EU fintech already bound by EU AML rules, which means the new AMLR and the AMLA supervisory framework apply directly to how it onboards customers and reports suspicious activity.',
    },
  },
  {
    id: 'kb-24-when-a-customer-disputes-a-card-charge',
    type: 'multiple-choice',
    shortLabel: 'Card dispute process',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question: 'In card payments, what best describes a chargeback?',
    options: [
      'refund issued proactively by the merchant to an unhappy customer',
      "reversal of a card transaction initiated by the cardholder's bank after a dispute, with the merchant given a chance to provide evidence.",
      'fee charged by the card network for each transaction processed',
      'An automated decline of any transaction above a risk threshold',
    ],
    correctAnswer:
      "reversal of a card transaction initiated by the cardholder's bank after a dispute, with the merchant given a chance to provide evidence.",
    mossInsight: {
      body: 'Chargebacks are the consumer protection safety net baked into card networks. When something goes wrong (non delivery, fraud, duplicate charges), the cardholder gets to pull the money back and the merchant has to fight to keep it. Losing too many hurts your fees and your standing with the network, and "friendly fraud" (customers disputing real purchases) is now the fastest growing category merchants are battling.',
      howMossHelps:
        'Moss gives finance real time visibility into every card transaction with clear owner, merchant, and approval context, so unfamiliar charges can be questioned and resolved easily.',
    },
  },
  {
    id: 'kb-25-putting-crypto-inside-the-rulebook',
    type: 'multiple-choice',
    shortLabel: 'EU Markets in Crypto-Assets',
    phase: 1,
    questionGroup: 'p1-knowledge-mcq',
    timerSeconds: 45,
    points: 10,
    question:
      "The EU's Markets in Crypto-Assets Regulation (MiCA), which became fully applicable in 2024, primarily does what?",
    options: [
      'Bans the issuance of stablecoins within the EU',
      'Introduces a harmonized EU licensing regime for crypto asset service providers and stablecoin issuers',
      'Classifies all crypto-assets as commodities under EU law',
      'Replaces national AML rules for all crypto firms with a single EU registration',
    ],
    correctAnswer:
      'Introduces a harmonized EU licensing regime for crypto asset service providers and stablecoin issuers',
    mossInsight: {
      body: 'MiCA is the EU dragging crypto into the same rulebook the rest of finance has lived in for years. Stablecoins, exchanges, custody, and crypto services now need licences, governance, and consumer protection standards, and once authorized a provider can passport across the EU. So crypto counterparties are starting to look a lot more like regulated financial institutions and a lot less like Wild West startups.',
      howMossHelps:
        'Moss runs on euro banking rails under BaFin supervision rather than crypto ones, so the licensing and conduct standards MiCA is bringing to crypto providers are already the baseline Moss operates under.',
    },
  },
  {
    id: 'epd-01',
    type: 'phishing-email',
    shortLabel: 'Sender Address',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'sender-address',
    phishingEmail: {
      senderName: 'TechSolutions GmbH',
      senderAddress: 'billing@techso1utions.de',
      replyTo: 'billing@techsolutions.de',
      subject: 'Invoice INV-2025-0341 – March Services',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice INV-2025-0341 for IT consulting services rendered in March 2025, totalling € 5,800.00. Payment is due by 15.04.2025 using the bank details on file. Please do not hesitate to reach out with any questions.\n\nKind regards,\nTechSolutions GmbH',
      correctFieldId: 'sender-address',
    },
    mossInsight: {
      body: 'Fraudsters often use lookalike domains that differ by just one letter. Easy to miss, but enough to redirect payments.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-02',
    type: 'phishing-email',
    shortLabel: 'Sender Address',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'sender-address',
    phishingEmail: {
      senderName: 'Moss Supplier',
      senderAddress: 'invoices@moss-supp1ier.com',
      replyTo: 'invoices@mosssupplier.com',
      subject: 'Invoice #8812 – Software License Renewal',
      body: 'Dear Finance Team,\n\nEnclosed is invoice #8812 for the software license renewal covering Q2 2025, totalling € 12,400.00. We kindly ask that payment be processed by 20.04.2025 as per our agreed terms. Thank you for your continued partnership.\n\nKind regards,\nMoss Supplier',
      correctFieldId: 'sender-address',
    },
    mossInsight: {
      body: 'Fraudsters often use lookalike domains that differ by just one letter. Easy to miss, but enough to redirect payments.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-03',
    type: 'phishing-email',
    shortLabel: 'Sender Address',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'sender-address',
    phishingEmail: {
      senderName: 'Europa Logistics GmbH',
      senderAddress: 'accounts@europalogistcs.de',
      replyTo: 'accounts@europalogistics.de',
      subject: 'Invoice EL-2025-0087 – Freight Services April',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice EL-2025-0087 for freight forwarding services provided in April 2025. The total amount of € 3,240.00 is due within 14 days of receipt. We value our ongoing business relationship and remain available for any queries.\n\nKind regards,\nEuropa Logistics GmbH',
      correctFieldId: 'sender-address',
    },
    mossInsight: {
      body: 'Fraudsters often use lookalike domains that differ by just one letter. Easy to miss, but enough to redirect payments.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-04',
    type: 'phishing-email',
    shortLabel: 'Sender Address',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'sender-address',
    phishingEmail: {
      senderName: 'GlobalTrade Supply B.V.',
      senderAddress: 'payment@globaltrade-supp1y.nl',
      replyTo: 'payment@globaltrade-supply.nl',
      subject: 'Invoice GT-NL-2025-192 – Trading Services',
      body: 'Dear Finance Team,\n\nPlease find attached invoice GT-NL-2025-192 for trading and procurement services delivered in Q1 2025, totalling € 9,750.00. Payment terms are 30 days net from the invoice date. We appreciate your prompt attention to this matter.\n\nKind regards,\nGlobalTrade Supply B.V.',
      correctFieldId: 'sender-address',
    },
    mossInsight: {
      body: 'Fraudsters often use lookalike domains that differ by just one letter. Easy to miss, but enough to redirect payments.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-05',
    type: 'phishing-email',
    shortLabel: 'Sender Address',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'sender-address',
    phishingEmail: {
      senderName: 'Accounting Hub Ltd',
      senderAddress: 'finance@accountting-hub.co.uk',
      replyTo: 'finance@accounting-hub.co.uk',
      subject: 'Monthly Statement – March 2025',
      body: 'Dear Finance Team,\n\nPlease find attached your monthly statement for March 2025. The outstanding balance of £ 6,320.00 is due for payment by 30.04.2025. Should you have any queries regarding the statement, please do not hesitate to get in touch.\n\nKind regards,\nAccounting Hub Ltd',
      correctFieldId: 'sender-address',
    },
    mossInsight: {
      body: 'Fraudsters often use lookalike domains that differ by just one letter. Easy to miss, but enough to redirect payments.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-06',
    type: 'phishing-email',
    shortLabel: 'Sender Address',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'sender-address',
    phishingEmail: {
      senderName: 'Payment Services GmbH',
      senderAddress: 'billing@paymentservlces.com',
      replyTo: 'billing@paymentservices.com',
      subject: 'Invoice PS-2025-0554 – Processing Fees',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice PS-2025-0554 for payment processing services rendered in March 2025. The total amount of € 1,890.00 is payable within 7 days of the invoice date. We look forward to your confirmation of receipt.\n\nKind regards,\nPayment Services GmbH',
      correctFieldId: 'sender-address',
    },
    mossInsight: {
      body: 'Fraudsters often use lookalike domains that differ by just one letter. Easy to miss, but enough to redirect payments.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-07',
    type: 'phishing-email',
    shortLabel: 'Sender Address',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'sender-address',
    phishingEmail: {
      senderName: 'Nordic Logistics B.V.',
      senderAddress: 'invoices@nordiclogistlcs.nl',
      replyTo: 'invoices@nordiclogistics.nl',
      subject: 'Invoice NL-2025-0334 – Warehouse & Distribution',
      body: 'Dear Finance Team,\n\nEnclosed please find invoice NL-2025-0334 for warehouse and distribution services provided in March 2025. The total amount of € 7,600.00 is due within 30 days net. We appreciate your continued trust in our services.\n\nKind regards,\nNordic Logistics B.V.',
      correctFieldId: 'sender-address',
    },
    mossInsight: {
      body: 'Fraudsters often use lookalike domains that differ by just one letter. Easy to miss, but enough to redirect payments.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-08',
    type: 'phishing-email',
    shortLabel: 'Sender Address',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'sender-address',
    phishingEmail: {
      senderName: 'Consultancy Group S.A.',
      senderAddress: 'noreply@consu1tancygroup.fr',
      replyTo: 'noreply@consultancygroup.fr',
      subject: 'Invoice CG-FR-2025-0219 – Advisory Services',
      body: 'Dear Finance Team,\n\nVeuillez trouver ci-joint la facture CG-FR-2025-0219 pour les services de conseil strategique fournis en mars 2025. Le montant de € 4,500.00 est a regler avant le 10.04.2025. Nous restons disponibles pour toute question.\n\nKind regards,\nConsultancy Group S.A.',
      correctFieldId: 'sender-address',
    },
    mossInsight: {
      body: 'Fraudsters often use lookalike domains that differ by just one letter. Easy to miss, but enough to redirect payments.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-09',
    type: 'phishing-email',
    shortLabel: 'Reply-To',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'reply-to',
    phishingEmail: {
      senderName: 'TechSolutions GmbH',
      senderAddress: 'billing@techsolutions.de',
      replyTo: 'billing@techsolutions-payments.net',
      subject: 'Invoice TS-2025-0456 – April Services',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice TS-2025-0456 for IT consulting services provided in April 2025. The total amount of € 6,200.00 is due by 15.04.2025. Payment details are as per our records on file.\n\nKind regards,\nTechSolutions GmbH',
      correctFieldId: 'reply-to',
    },
    mossInsight: {
      body: 'A different reply-to address can silently redirect communication outside trusted channels, a key step in many fraud cases.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-10',
    type: 'phishing-email',
    shortLabel: 'Reply-To',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'reply-to',
    phishingEmail: {
      senderName: 'Supplier Co B.V.',
      senderAddress: 'invoices@supplierco.nl',
      replyTo: 'invoices.payment@gmail.com',
      subject: 'Invoice SC-2025-0187 – Supply Chain Services',
      body: 'Dear Finance Team,\n\nEnclosed you will find invoice SC-2025-0187 for supply chain management services delivered in Q1 2025, totalling € 11,300.00. Payment terms are 30 days net from the invoice date. We appreciate your prompt processing.\n\nKind regards,\nSupplier Co B.V.',
      correctFieldId: 'reply-to',
    },
    mossInsight: {
      body: 'A different reply-to address can silently redirect communication outside trusted channels, a key step in many fraud cases.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-11',
    type: 'phishing-email',
    shortLabel: 'Reply-To',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'reply-to',
    phishingEmail: {
      senderName: 'Global Logistics GmbH',
      senderAddress: 'accounts@globallogistics.de',
      replyTo: 'accounts@g1oballogistics.de',
      subject: 'Invoice GL-2025-0623 – Transport & Forwarding',
      body: 'Dear Finance Team,\n\nPlease find attached invoice GL-2025-0623 for international transport and forwarding services rendered in March 2025. The total amount of € 8,450.00 is due by 20.04.2025. Please confirm receipt at your earliest convenience.\n\nKind regards,\nGlobal Logistics GmbH',
      correctFieldId: 'reply-to',
    },
    mossInsight: {
      body: 'A different reply-to address can silently redirect communication outside trusted channels, a key step in many fraud cases.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-12',
    type: 'phishing-email',
    shortLabel: 'Reply-To',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'reply-to',
    phishingEmail: {
      senderName: 'Europa Consult S.A.S.',
      senderAddress: 'finance@europaconsult.fr',
      replyTo: 'europe.consulting.payments@outlook.com',
      subject: 'Invoice EC-FR-2025-0098 – Strategic Consulting',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice EC-FR-2025-0098 for strategic consulting services provided in Q1 2025. The total amount of € 14,000.00 is due within 30 days of the invoice date. We value our partnership and remain available for any questions.\n\nKind regards,\nEuropa Consult S.A.S.',
      correctFieldId: 'reply-to',
    },
    mossInsight: {
      body: 'A different reply-to address can silently redirect communication outside trusted channels, a key step in many fraud cases.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-13',
    type: 'phishing-email',
    shortLabel: 'Reply-To',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'reply-to',
    phishingEmail: {
      senderName: 'Vendor Services Ltd',
      senderAddress: 'payments@vendorservices.co.uk',
      replyTo: 'payments@vendorservlces.co.uk',
      subject: 'Invoice VS-UK-2025-0311 – Vendor Management',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice VS-UK-2025-0311 for vendor management services delivered in Q1 2025. The total amount of £ 9,800.00 is due within 14 days of the invoice date. Do not hesitate to contact us with any queries.\n\nKind regards,\nVendor Services Ltd',
      correctFieldId: 'reply-to',
    },
    mossInsight: {
      body: 'A different reply-to address can silently redirect communication outside trusted channels, a key step in many fraud cases.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-14',
    type: 'phishing-email',
    shortLabel: 'Reply-To',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'reply-to',
    phishingEmail: {
      senderName: 'Moss Partner GmbH',
      senderAddress: 'billing@mosspartner.de',
      replyTo: 'invoice.remittance@protonmail.com',
      subject: 'Invoice MP-2025-0774 – Partnership Services',
      body: 'Dear Finance Team,\n\nEnclosed is invoice MP-2025-0774 for partnership and consulting services rendered in March 2025, totalling € 5,400.00. We kindly request remittance within 14 days as per our agreed terms. Thank you for your continued collaboration.\n\nKind regards,\nMoss Partner GmbH',
      correctFieldId: 'reply-to',
    },
    mossInsight: {
      body: 'A different reply-to address can silently redirect communication outside trusted channels, a key step in many fraud cases.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-15',
    type: 'phishing-email',
    shortLabel: 'Reply-To',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'reply-to',
    phishingEmail: {
      senderName: 'Nordic Logistics B.V.',
      senderAddress: 'accounting@nordiclogistics.nl',
      replyTo: 'accounting@nordiclog1stics.nl',
      subject: 'Invoice NL-2025-0445 – Logistics Q1 2025',
      body: 'Dear Finance Team,\n\nPlease find attached invoice NL-2025-0445 for logistics services provided throughout Q1 2025. The total amount of € 16,200.00 is payable within 45 days net. We value your ongoing partnership and remain at your disposal for any questions.\n\nKind regards,\nNordic Logistics B.V.',
      correctFieldId: 'reply-to',
    },
    mossInsight: {
      body: 'A different reply-to address can silently redirect communication outside trusted channels, a key step in many fraud cases.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-16',
    type: 'phishing-email',
    shortLabel: 'Reply-To',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'reply-to',
    phishingEmail: {
      senderName: 'Trading House GmbH',
      senderAddress: 'invoices@tradinghouse.de',
      replyTo: 'invoices@trading-house-payments.com',
      subject: 'Invoice TH-2025-0882 – Import/Export Services',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice TH-2025-0882 for import/export brokerage services provided in March 2025, totalling € 7,800.00. Payment is due by 30.04.2025. We look forward to your confirmation.\n\nKind regards,\nTrading House GmbH',
      correctFieldId: 'reply-to',
    },
    mossInsight: {
      body: 'A different reply-to address can silently redirect communication outside trusted channels, a key step in many fraud cases.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-17',
    type: 'phishing-email',
    shortLabel: 'Subject Line',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'subject',
    phishingEmail: {
      senderName: 'TechSolutions GmbH',
      senderAddress: 'billing@techsolutions.de',
      replyTo: 'billing@techsolutions.de',
      subject: 'URGENT: Invoice INV-2025-0821 overdue – Immediate action required',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice INV-2025-0821 for IT consulting services provided in March 2025. The total amount of € 5,800.00 is due as per our agreed payment terms. Please do not hesitate to contact us with any questions.\n\nKind regards,\nTechSolutions GmbH',
      correctFieldId: 'subject',
    },
    mossInsight: {
      body: 'No legitimate invoice needs a subject line that makes your heart race. Urgency in the header is there to short-circuit your judgment. Take a breath first.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-18',
    type: 'phishing-email',
    shortLabel: 'Subject Line',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'subject',
    phishingEmail: {
      senderName: 'Supplier Co B.V.',
      senderAddress: 'invoices@supplierco.nl',
      replyTo: 'invoices@supplierco.nl',
      subject: 'FINAL NOTICE: Outstanding balance – Account suspension in 24 hours',
      body: 'Dear Finance Team,\n\nEnclosed is invoice SC-2025-0234 for supply chain management services delivered in February 2025. The total amount of € 9,200.00 is payable within 30 days of the invoice date. We appreciate your continued partnership.\n\nKind regards,\nSupplier Co B.V.',
      correctFieldId: 'subject',
    },
    mossInsight: {
      body: 'No legitimate invoice needs a subject line that makes your heart race. Urgency in the header is there to short-circuit your judgment. Take a breath first.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-19',
    type: 'phishing-email',
    shortLabel: 'Subject Line',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'subject',
    phishingEmail: {
      senderName: 'Europa Logistics GmbH',
      senderAddress: 'accounts@europalogistics.de',
      replyTo: 'accounts@europalogistics.de',
      subject: 'ACTION REQUIRED: Confirm wire transfer TODAY or forfeit early payment discount',
      body: 'Dear Finance Team,\n\nPlease find attached invoice EL-2025-0091 for freight forwarding services provided in March 2025. The total amount of € 4,100.00 is due as per our standard payment terms. We remain available should you have any queries.\n\nKind regards,\nEuropa Logistics GmbH',
      correctFieldId: 'subject',
    },
    mossInsight: {
      body: 'No legitimate invoice needs a subject line that makes your heart race. Urgency in the header is there to short-circuit your judgment. Take a breath first.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-20',
    type: 'phishing-email',
    shortLabel: 'Subject Line',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'subject',
    phishingEmail: {
      senderName: 'Global Consult S.A.S.',
      senderAddress: 'finance@globalconsult.fr',
      replyTo: 'finance@globalconsult.fr',
      subject: 'CRITICAL: Payment 48h overdue – Late fees initiated immediately',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice GC-FR-2025-0112 for consulting services provided in March 2025. The total amount of € 12,500.00 is due within 30 days as per our agreed terms. Please do not hesitate to reach out with any questions.\n\nKind regards,\nGlobal Consult S.A.S.',
      correctFieldId: 'subject',
    },
    mossInsight: {
      body: 'No legitimate invoice needs a subject line that makes your heart race. Urgency in the header is there to short-circuit your judgment. Take a breath first.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-21',
    type: 'phishing-email',
    shortLabel: 'Subject Line',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'subject',
    phishingEmail: {
      senderName: 'Vendor Services Ltd',
      senderAddress: 'payments@vendorservices.co.uk',
      replyTo: 'payments@vendorservices.co.uk',
      subject: 'RE: Outstanding invoice – Must be settled before close of business',
      body: 'Dear Finance Team,\n\nEnclosed is invoice VS-UK-2025-0388 for vendor management services rendered in Q1 2025. The total amount of £ 7,300.00 is due as per our agreed payment terms. Please confirm receipt at your convenience.\n\nKind regards,\nVendor Services Ltd',
      correctFieldId: 'subject',
    },
    mossInsight: {
      body: 'No legitimate invoice needs a subject line that makes your heart race. Urgency in the header is there to short-circuit your judgment. Take a breath first.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-22',
    type: 'phishing-email',
    shortLabel: 'Subject Line',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'subject',
    phishingEmail: {
      senderName: 'Nordic Logistics B.V.',
      senderAddress: 'billing@nordiclogistics.nl',
      replyTo: 'billing@nordiclogistics.nl',
      subject: 'LAST REMINDER: Invoice NL-7734 – Legal proceedings being initiated',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice NL-2025-0501 for warehousing and distribution services provided in Q1 2025. The total amount of € 18,400.00 is due within 30 days net. We look forward to your prompt processing.\n\nKind regards,\nNordic Logistics B.V.',
      correctFieldId: 'subject',
    },
    mossInsight: {
      body: 'No legitimate invoice needs a subject line that makes your heart race. Urgency in the header is there to short-circuit your judgment. Take a breath first.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-23',
    type: 'phishing-email',
    shortLabel: 'Subject Line',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'subject',
    phishingEmail: {
      senderName: 'Trading House GmbH',
      senderAddress: 'invoices@tradinghouse.de',
      replyTo: 'invoices@tradinghouse.de',
      subject: 'Time-sensitive: Wire transfer confirmation needed within 2 hours',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice TH-2025-0891 for import brokerage services rendered in March 2025. The total amount of € 6,750.00 is payable within 30 days of the invoice date. Thank you for your continued trust.\n\nKind regards,\nTrading House GmbH',
      correctFieldId: 'subject',
    },
    mossInsight: {
      body: 'No legitimate invoice needs a subject line that makes your heart race. Urgency in the header is there to short-circuit your judgment. Take a breath first.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-24',
    type: 'phishing-email',
    shortLabel: 'Subject Line',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'subject',
    phishingEmail: {
      senderName: 'Moss Partner GmbH',
      senderAddress: 'accounts@mosspartner.de',
      replyTo: 'accounts@mosspartner.de',
      subject: 'ESCALATED: Unpaid balance – Your CFO has been notified, transfer immediately',
      body: 'Dear Finance Team,\n\nPlease find enclosed invoice MP-2025-0805 for partnership consulting services rendered in March 2025, totalling € 8,900.00. Payment is due as per our standard terms. We value our collaboration and remain available for any questions.\n\nKind regards,\nMoss Partner GmbH',
      correctFieldId: 'subject',
    },
    mossInsight: {
      body: 'No legitimate invoice needs a subject line that makes your heart race. Urgency in the header is there to short-circuit your judgment. Take a breath first.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-25',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'TechSolutions GmbH',
      senderAddress: 'billing@techsolutions.de',
      replyTo: 'billing@techsolutions.de',
      subject: 'Invoice INV-2025-0341 – Access Required',
      body: 'Dear Finance Team,\n\nPlease review and approve invoice INV-2025-0341 via our secure portal: techsolutions-invoiceportal.net/inv/0341.pdf. Amount: € 5,800.00.\n\nKind regards,\nTechSolutions GmbH',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-26',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Supplier Co B.V.',
      senderAddress: 'invoices@supplierco.nl',
      replyTo: 'invoices@supplierco.nl',
      subject: 'Invoice SC-2025-0412 – Download Required',
      body: 'Dear Finance Team,\n\nKindly download invoice SC-2025-0412 at: docs-supplierco.online/invoice-march2025.pdf. Total: € 9,200.00.\n\nKind regards,\nSupplier Co B.V.',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-27',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Europa Logistics GmbH',
      senderAddress: 'accounts@europalogistics.de',
      replyTo: 'accounts@europalogistics.de',
      subject: 'Invoice EL-2025-0087 – Action Required',
      body: 'Dear Finance Team,\n\nPlease access invoice EL-2025-0087 for your records: europalogistlcs.de/invoices/EL2025-0087.pdf. Amount: € 3,240.00.\n\nKind regards,\nEuropa Logistics GmbH',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-28',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Global Consult S.A.S.',
      senderAddress: 'finance@globalconsult.fr',
      replyTo: 'finance@globalconsult.fr',
      subject: 'Invoice GC-FR-2025-0219 – Review & Approve',
      body: 'Dear Finance Team,\n\nTo view and approve invoice GC-FR-2025-0219, click here: invoice-payment.globalconsult-portal.com/GC2025-0219. Montant: € 14,000.00.\n\nKind regards,\nGlobal Consult S.A.S.',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-29',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Nordic Logistics B.V.',
      senderAddress: 'billing@nordiclogistics.nl',
      replyTo: 'billing@nordiclogistics.nl',
      subject: 'Invoice NL-2025-0334 – Awaiting Confirmation',
      body: 'Dear Finance Team,\n\nPlease confirm receipt and approval of invoice NL-2025-0334 at: paymentconfirm-nordic.net/ref=NL0334. Amount: € 7,600.00.\n\nKind regards,\nNordic Logistics B.V.',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-30',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Vendor Services Ltd',
      senderAddress: 'payments@vendorservices.co.uk',
      replyTo: 'payments@vendorservices.co.uk',
      subject: 'Invoice VS-UK-2025-0311 – Process via Portal',
      body: 'Dear Finance Team,\n\nPlease process invoice VS-UK-2025-0311 through our updated vendor portal: accountingservices-vendorco.com/download/VS-UK-2025-0311. Amount: £ 9,800.00.\n\nKind regards,\nVendor Services Ltd',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-31',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'TechSolutions GmbH',
      senderAddress: 'billing@techsolutions.de',
      replyTo: 'billing@techsolutions.de',
      subject: 'Invoice INV-2025-0872 – Payment Request',
      body: 'Dear Finance Team,\n\nThis invoice must be settled within 2 hours to avoid immediate contract termination. Please wire € 8,400.00 to our account now. No further correspondence will be entertained.\n\nKind regards,\nTechSolutions GmbH',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-32',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Supplier Co B.V.',
      senderAddress: 'invoices@supplierco.nl',
      replyTo: 'invoices@supplierco.nl',
      subject: 'Invoice SC-2025-0561 – Pending',
      body: 'Dear Finance Team,\n\nYour CFO has been personally notified of this outstanding balance. An immediate wire transfer of € 11,300.00 is required to prevent escalation to our legal department.\n\nKind regards,\nSupplier Co B.V.',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-33',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Global Logistics GmbH',
      senderAddress: 'accounts@globallogistics.de',
      replyTo: 'accounts@globallogistics.de',
      subject: 'Invoice GL-2025-0623 – Final Contact',
      body: 'Dear Finance Team,\n\nWe have initiated legal proceedings. The outstanding amount of € 8,450.00 must be transferred today to halt proceedings. Failure to comply will result in additional legal fees.\n\nKind regards,\nGlobal Logistics GmbH',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-34',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Europa Consult S.A.S.',
      senderAddress: 'finance@europaconsult.fr',
      replyTo: 'finance@europaconsult.fr',
      subject: 'Invoice EC-FR-2025-0107 – Settlement Required',
      body: 'Dear Finance Team,\n\nThis is your final opportunity to settle invoice EC-FR-2025-0107 before we escalate to our debt collection agency. Payment of € 14,000.00 must be received by midnight tonight.\n\nKind regards,\nEuropa Consult S.A.S.',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-35',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Moss Partner GmbH',
      senderAddress: 'billing@mosspartner.de',
      replyTo: 'billing@mosspartner.de',
      subject: 'Invoice MP-2025-0812 – Overdue',
      body: 'Dear Finance Team,\n\nOur automated system will flag your account for suspension at midnight unless payment of € 5,400.00 is received. Please transfer immediately and forward confirmation to avoid service interruption.\n\nKind regards,\nMoss Partner GmbH',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-36',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'TechSolutions GmbH',
      senderAddress: 'billing@techsolutions.de',
      replyTo: 'billing@techsolutions.de',
      subject: 'Invoice INV-2025-0891 – Updated Payment Details',
      body: 'Dear Finance Team,\n\nPlease note our banking details have changed effective immediately. Please use new IBAN: DE44 5001 0517 8832 1560 00 for all future payments, including invoice INV-2025-0891 (€ 6,200.00). Disregard previous account details.\n\nKind regards,\nTechSolutions GmbH',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-37',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Nordic Logistics B.V.',
      senderAddress: 'accounts@nordiclogistics.nl',
      replyTo: 'accounts@nordiclogistics.nl',
      subject: 'Invoice NL-2025-0501 – Banking Update',
      body: 'Dear Finance Team,\n\nDue to our bank migration, all payments must now be directed to our new account: NL91 ABNA 0417 1643 00. Please update your records and process invoice NL-2025-0501 (€ 16,200.00) accordingly.\n\nKind regards,\nNordic Logistics B.V.',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-38',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Vendor Services Ltd',
      senderAddress: 'payments@vendorservices.co.uk',
      replyTo: 'payments@vendorservices.co.uk',
      subject: 'Invoice VS-UK-2025-0444 – New Bank Details',
      body: 'Dear Finance Team,\n\nWe have switched banking providers. Please use our new bank account GB29 NWBK 6016 1331 9268 19 for invoice VS-UK-2025-0444 (£ 9,800.00) and all future transactions. Old account details are no longer valid.\n\nKind regards,\nVendor Services Ltd',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-39',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Global Consult S.A.S.',
      senderAddress: 'finance@globalconsult.fr',
      replyTo: 'finance@globalconsult.fr',
      subject: 'Invoice GC-FR-2025-0231 – Payment Details Revised',
      body: 'Dear Finance Team,\n\nOur finance department has updated our payment details. Please use new IBAN: FR76 3000 6000 0112 3456 7890 189 for invoice GC-FR-2025-0231 (€ 14,000.00). Please do not transfer to any other account.\n\nKind regards,\nGlobal Consult S.A.S.',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'epd-40',
    type: 'phishing-email',
    shortLabel: 'Email Body',
    phase: 2,
    questionGroup: 'q14-phishing-email',
    timerSeconds: 45,
    points: 10,
    question: 'Review the email carefully and select the element that looks suspicious.',
    options: [],
    correctAnswer: 'body',
    phishingEmail: {
      senderName: 'Europa Logistics GmbH',
      senderAddress: 'billing@europalogistics.de',
      replyTo: 'billing@europalogistics.de',
      subject: 'Invoice EL-2025-0134 – IBAN Correction',
      body: 'Dear Finance Team,\n\nPlease disregard our previous payment instructions. Due to an error, please use our corrected bank account: BE68 5390 0754 7034 for invoice EL-2025-0134 (€ 4,100.00). We apologise for any inconvenience.\n\nKind regards,\nEuropa Logistics GmbH',
      correctFieldId: 'body',
    },
    mossInsight: {
      body: 'Fraudsters use tactics like suspicious links, artificial urgency, or fake bank detail changes in the body to rush you past your own judgment. When an email feels off, trust that instinct.',
      howMossHelps:
        'Moss prevents email-driven fraud by enforcing centrally stored supplier data, automatic mismatch detection, approval workflows, and full visibility, ensuring suspicious changes are flagged before any payment is executed.',
    },
  },
  {
    id: 'mdc-01',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Müller & Partner GmbH',
        address: 'Friedrichstraße 12, 10117 Berlin',
        vatId: 'DE284719053',
        iban: 'DE32 1001 0010 0482 7391 00',
        invoiceNumber: 'INV-2025-0031',
        date: '03.01.2025',
        description: 'HR consulting services - January 2025',
        amount: '€ 4,500.00',
      },
      masterData: {
        supplierName: 'Müller & Partners GmbH',
        address: 'Friedrichstraße 12, 10117 Berlin',
        vatId: 'DE284719053',
        iban: 'DE32 1001 0010 0482 7391 00',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'Fraudsters often rely on tiny spelling changes, because most checks are visual, not systematic.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-02',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Benelux Sollutions B.V.',
        address: 'Coolsingel 22, 3011 AD Rotterdam',
        vatId: 'NL839274561B01',
        iban: 'NL24 INGB 0674 9238 16',
        invoiceNumber: 'INV-2025-0087',
        date: '17.01.2025',
        description: 'Import/export consulting - January 2025',
        amount: '€ 7,200.00',
      },
      masterData: {
        supplierName: 'Benelux Solutions B.V.',
        address: 'Coolsingel 22, 3011 AD Rotterdam',
        vatId: 'NL839274561B01',
        iban: 'NL24 INGB 0674 9238 16',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'Fraudsters often rely on tiny spelling changes, because most checks are visual, not systematic.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-03',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Whitemore Advisory Ltd',
        address: '32 Moorgate, London EC2R 6AZ',
        vatId: 'GB391028457',
        iban: 'GB72 BARC 2014 7083 4729 19',
        invoiceNumber: 'INV-2025-0142',
        date: '24.01.2025',
        description: 'Strategic advisory services - Q1 2025',
        amount: '£ 8,400.00',
      },
      masterData: {
        supplierName: 'Whitmore Advisory Ltd',
        address: '32 Moorgate, London EC2R 6AZ',
        vatId: 'GB391028457',
        iban: 'GB72 BARC 2014 7083 4729 19',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'Fraudsters often rely on tiny spelling changes, because most checks are visual, not systematic.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-04',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Digtalwerk GmbH',
        address: 'Rosenheimer Straße 143, 81671 München',
        vatId: 'DE319274085',
        iban: 'DE73 7002 0270 0019 3748 21',
        invoiceNumber: 'INV-2025-0198',
        date: '06.02.2025',
        description: 'Digital transformation consulting - February 2025',
        amount: '€ 11,800.00',
      },
      masterData: {
        supplierName: 'Digitalwerk GmbH',
        address: 'Rosenheimer Straße 143, 81671 München',
        vatId: 'DE319274085',
        iban: 'DE73 7002 0270 0019 3748 21',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'Fraudsters often rely on tiny spelling changes, because most checks are visual, not systematic.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-05',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'NordTech Servlces GmbH',
        address: 'Kehrwieder 9, 20457 Hamburg',
        vatId: 'DE298174530',
        iban: 'DE91 2004 0060 0123 9847 00',
        invoiceNumber: 'INV-2025-0243',
        date: '14.02.2025',
        description: 'Network infrastructure services - Q1 2025',
        amount: '€ 16,300.00',
      },
      masterData: {
        supplierName: 'NordTech Services GmbH',
        address: 'Kehrwieder 9, 20457 Hamburg',
        vatId: 'DE298174530',
        iban: 'DE91 2004 0060 0123 9847 00',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'Fraudsters often rely on tiny spelling changes, because most checks are visual, not systematic.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-06',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Greenfield Analytlcs Ltd',
        address: '18 Aldersgate Street, London EC1A 4JQ',
        vatId: 'GB427308192',
        iban: 'GB33 HSBC 4031 2849 7302 81',
        invoiceNumber: 'INV-2025-0319',
        date: '28.02.2025',
        description: 'Business analytics services - February 2025',
        amount: '£ 12,700.00',
      },
      masterData: {
        supplierName: 'Greenfield Analytics Ltd',
        address: '18 Aldersgate Street, London EC1A 4JQ',
        vatId: 'GB427308192',
        iban: 'GB33 HSBC 4031 2849 7302 81',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'Fraudsters often rely on tiny spelling changes, because most checks are visual, not systematic.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-07',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Beratunghaus GmbH',
        address: 'Kaiserstraße 29, 60311 Frankfurt am Main',
        vatId: 'DE274893016',
        iban: 'DE58 5004 0000 0374 8291 00',
        invoiceNumber: 'INV-2025-0402',
        date: '11.03.2025',
        description: 'Management consulting - March 2025',
        amount: '€ 9,600.00',
      },
      masterData: {
        supplierName: 'Beratungshaus GmbH',
        address: 'Kaiserstraße 29, 60311 Frankfurt am Main',
        vatId: 'DE274893016',
        iban: 'DE58 5004 0000 0374 8291 00',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'Fraudsters often rely on tiny spelling changes, because most checks are visual, not systematic.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-08',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Optifow B.V.',
        address: 'Prinsengracht 263, 1016 GV Amsterdam',
        vatId: 'NL847293016B01',
        iban: 'NL61 ABNA 0437 1928 30',
        invoiceNumber: 'INV-2025-0461',
        date: '19.03.2025',
        description: 'Architecture & design consulting - Q1 2025',
        amount: '€ 5,300.00',
      },
      masterData: {
        supplierName: 'Optiflow B.V.',
        address: 'Prinsengracht 263, 1016 GV Amsterdam',
        vatId: 'NL847293016B01',
        iban: 'NL61 ABNA 0437 1928 30',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'Fraudsters often rely on tiny spelling changes, because most checks are visual, not systematic.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-09',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Alpha Consulting',
        address: 'Goethestraße 18, 60313 Frankfurt am Main',
        vatId: 'DE298430165',
        iban: 'DE44 5004 0000 0123 7849 00',
        invoiceNumber: 'INV-2025-0019',
        date: '02.01.2025',
        description: 'Tax advisory services - Q1 2025',
        amount: '€ 3,800.00',
      },
      masterData: {
        supplierName: 'Alpha Consulting GmbH',
        address: 'Goethestraße 18, 60313 Frankfurt am Main',
        vatId: 'DE298430165',
        iban: 'DE44 5004 0000 0123 7849 00',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'The legal entity (e.g. GmbH) defines who you are contracting with, missing it creates legal and liability ambiguity.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-10',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'PaySmart N.V.',
        address: 'Prins Hendrikkade 100, 1011 AC Amsterdam',
        vatId: 'NL862394710B01',
        iban: 'NL18 RABO 0107 4839 21',
        invoiceNumber: 'INV-2025-0053',
        date: '09.01.2025',
        description: 'Payment processing services - January 2025',
        amount: '€ 6,700.00',
      },
      masterData: {
        supplierName: 'PaySmart B.V.',
        address: 'Prins Hendrikkade 100, 1011 AC Amsterdam',
        vatId: 'NL862394710B01',
        iban: 'NL18 RABO 0107 4839 21',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'The legal entity (e.g. GmbH) defines who you are contracting with, missing it creates legal and liability ambiguity.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-11',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Hartwell Limited',
        address: '22 Hanover Square, London W1S 1JD',
        vatId: 'GB314829037',
        iban: 'GB57 NWBK 6016 1331 4829 73',
        invoiceNumber: 'INV-2025-0119',
        date: '21.01.2025',
        description: 'Property management consulting - Q1 2025',
        amount: '£ 6,100.00',
      },
      masterData: {
        supplierName: 'Hartwell Ltd',
        address: '22 Hanover Square, London W1S 1JD',
        vatId: 'GB314829037',
        iban: 'GB57 NWBK 6016 1331 4829 73',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'The legal entity (e.g. GmbH) defines who you are contracting with, missing it creates legal and liability ambiguity.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-12',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Systemhaus GmbH',
        address: 'Theo-Champion-Straße 1, 40549 Düsseldorf',
        vatId: 'DE312048739',
        iban: 'DE62 3007 0024 0123 4567 89',
        invoiceNumber: 'INV-2025-0167',
        date: '30.01.2025',
        description: 'ERP implementation services - Q1 2025',
        amount: '€ 28,500.00',
      },
      masterData: {
        supplierName: 'Systemhaus AG',
        address: 'Theo-Champion-Straße 1, 40549 Düsseldorf',
        vatId: 'DE312048739',
        iban: 'DE62 3007 0024 0123 4567 89',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'The legal entity (e.g. GmbH) defines who you are contracting with, missing it creates legal and liability ambiguity.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-13',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'CloudFive',
        address: 'Weena 70, 3012 CM Rotterdam',
        vatId: 'NL857302194B01',
        iban: 'NL93 INGB 0003 4729 18',
        invoiceNumber: 'INV-2025-0221',
        date: '06.02.2025',
        description: 'Cloud hosting services - February 2025',
        amount: '€ 4,200.00',
      },
      masterData: {
        supplierName: 'CloudFive B.V.',
        address: 'Weena 70, 3012 CM Rotterdam',
        vatId: 'NL857302194B01',
        iban: 'NL93 INGB 0003 4729 18',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'The legal entity (e.g. GmbH) defines who you are contracting with, missing it creates legal and liability ambiguity.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-14',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Robertson & Co',
        address: '1 Bartholomew Lane, London EC2N 2AX',
        vatId: 'GB419302857',
        iban: 'GB92 LLOY 3094 7608 4729 31',
        invoiceNumber: 'INV-2025-0288',
        date: '18.02.2025',
        description: 'Financial restructuring advisory - February 2025',
        amount: '£ 17,400.00',
      },
      masterData: {
        supplierName: 'Robertson & Co Ltd',
        address: '1 Bartholomew Lane, London EC2N 2AX',
        vatId: 'GB419302857',
        iban: 'GB92 LLOY 3094 7608 4729 31',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'The legal entity (e.g. GmbH) defines who you are contracting with, missing it creates legal and liability ambiguity.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-15',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Kreuzberg Digital GmbH',
        address: 'Bergmannstraße 57, 10961 Berlin',
        vatId: 'DE294817360',
        iban: 'DE82 1001 0010 0394 8172 00',
        invoiceNumber: 'INV-2025-0344',
        date: '25.02.2025',
        description: 'Web development services - February 2025',
        amount: '€ 3,900.00',
      },
      masterData: {
        supplierName: 'Kreuzberg Digital UG',
        address: 'Bergmannstraße 57, 10961 Berlin',
        vatId: 'DE294817360',
        iban: 'DE82 1001 0010 0394 8172 00',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'The legal entity (e.g. GmbH) defines who you are contracting with, missing it creates legal and liability ambiguity.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-16',
    type: 'invoice-master-data',
    shortLabel: 'Supplier Name',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-supplier-name',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Veritas Partners S.A.',
        address: '5 Rue du Faubourg Saint-Honoré, 75008 Paris',
        vatId: 'FR75482931067',
        iban: 'FR76 1027 8022 2874 6291 0300 483',
        invoiceNumber: 'INV-2025-0383',
        date: '05.03.2025',
        description: 'Corporate restructuring services - Q1 2025',
        amount: '€ 14,600.00',
      },
      masterData: {
        supplierName: 'Veritas Partners S.A.S.',
        address: '5 Rue du Faubourg Saint-Honoré, 75008 Paris',
        vatId: 'FR75482931067',
        iban: 'FR76 1027 8022 2874 6291 0300 483',
      },
      correctFieldId: 'invoice-supplier-name',
    },
    mossInsight: {
      body: 'The legal entity (e.g. GmbH) defines who you are contracting with, missing it creates legal and liability ambiguity.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-17',
    type: 'invoice-master-data',
    shortLabel: 'Address Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-address',
    invoiceMasterData: {
      invoice: {
        supplierName: 'TechSolutions GmbH',
        address: 'Friedrichstraße 45, 10117 Berlin',
        vatId: 'DE298345102',
        iban: 'DE12 5001 0517 9384 2610 44',
        invoiceNumber: 'INV-2025-0847',
        date: '28.03.2025',
        description: 'IT consulting services - March 2025',
        amount: '€ 8,400.00',
      },
      masterData: {
        supplierName: 'TechSolutions GmbH',
        address: 'Münchener Straße 14, 80333 München',
        vatId: 'DE312784650',
        iban: 'DE71 7002 0270 0012 3456 78',
      },
      correctFieldId: 'invoice-address',
    },
    mossInsight: {
      body: "Suppliers don't quietly move offices. If the address on the invoice doesn't match your master data, something changed and nobody told you. Find out why before you approve.",
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-18',
    type: 'invoice-master-data',
    shortLabel: 'Address Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-address',
    invoiceMasterData: {
      invoice: {
        supplierName: 'DataVision B.V.',
        address: 'Marktstraat 5, 1011 PX Amsterdam',
        vatId: 'NL854612903B01',
        iban: 'NL91 ABNA 0417 1643 00',
        invoiceNumber: 'INV-2025-0312',
        date: '15.01.2025',
        description: 'Software development services - January 2025',
        amount: '€ 3,600.00',
      },
      masterData: {
        supplierName: 'DataVision B.V.',
        address: 'Marktstraat 5, 3011 AM Rotterdam',
        vatId: 'NL854612903B01',
        iban: 'NL91 ABNA 0417 1643 00',
      },
      correctFieldId: 'invoice-address',
    },
    mossInsight: {
      body: "Suppliers don't quietly move offices. If the address on the invoice doesn't match your master data, something changed and nobody told you. Find out why before you approve.",
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-19',
    type: 'invoice-master-data',
    shortLabel: 'Address Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-address',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Meridian Consulting Ltd',
        address: '14 Baker Street, London W1U 6TY',
        vatId: 'GB312847291',
        iban: 'GB29 NWBK 6016 1331 9268 19',
        invoiceNumber: 'INV-2025-1183',
        date: '12.02.2025',
        description: 'Legal advisory services - Q1 2025',
        amount: '£ 5,200.00',
      },
      masterData: {
        supplierName: 'Meridian Consulting Ltd',
        address: '14 Cannon Street, London EC4M 6XR',
        vatId: 'GB312847291',
        iban: 'GB29 NWBK 6016 1331 9268 19',
      },
      correctFieldId: 'invoice-address',
    },
    mossInsight: {
      body: "Suppliers don't quietly move offices. If the address on the invoice doesn't match your master data, something changed and nobody told you. Find out why before you approve.",
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-20',
    type: 'invoice-master-data',
    shortLabel: 'Address Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-address',
    invoiceMasterData: {
      invoice: {
        supplierName: 'LogisTrans GmbH',
        address: 'Berliner Allee 23, 40212 Düsseldorf',
        vatId: 'DE287341092',
        iban: 'DE45 3007 0010 0123 4567 00',
        invoiceNumber: 'INV-2025-0489',
        date: '05.03.2025',
        description: 'Freight & logistics services - March 2025',
        amount: '€ 12,750.00',
      },
      masterData: {
        supplierName: 'LogisTrans GmbH',
        address: 'Berliner Allee 23, 40213 Düsseldorf',
        vatId: 'DE287341092',
        iban: 'DE45 3007 0010 0123 4567 00',
      },
      correctFieldId: 'invoice-address',
    },
    mossInsight: {
      body: "Suppliers don't quietly move offices. If the address on the invoice doesn't match your master data, something changed and nobody told you. Find out why before you approve.",
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-21',
    type: 'invoice-master-data',
    shortLabel: 'Address Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-address',
    invoiceMasterData: {
      invoice: {
        supplierName: 'FinanceFlow B.V.',
        address: 'Keizersgracht 125, 1015 CW Amsterdam',
        vatId: 'NL823749516B01',
        iban: 'NL44 RABO 0123 4567 89',
        invoiceNumber: 'INV-2025-0924',
        date: '18.04.2025',
        description: 'Accounting services - April 2025',
        amount: '€ 4,800.00',
      },
      masterData: {
        supplierName: 'FinanceFlow B.V.',
        address: 'Keizersgracht 152, 1015 CW Amsterdam',
        vatId: 'NL823749516B01',
        iban: 'NL44 RABO 0123 4567 89',
      },
      correctFieldId: 'invoice-address',
    },
    mossInsight: {
      body: "Suppliers don't quietly move offices. If the address on the invoice doesn't match your master data, something changed and nobody told you. Find out why before you approve.",
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-22',
    type: 'invoice-master-data',
    shortLabel: 'Address Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-address',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Northern Risk Management Ltd',
        address: '8 Victoria Square, Leeds LS1 5HD',
        vatId: 'GB428193057',
        iban: 'GB24 BARC 2000 0055 7799 11',
        invoiceNumber: 'INV-2025-2210',
        date: '22.05.2025',
        description: 'IT risk assessment services - Q2 2025',
        amount: '£ 7,800.00',
      },
      masterData: {
        supplierName: 'Northern Risk Management Ltd',
        address: '8 Victoria Square, Birmingham B1 1BD',
        vatId: 'GB428193057',
        iban: 'GB24 BARC 2000 0055 7799 11',
      },
      correctFieldId: 'invoice-address',
    },
    mossInsight: {
      body: "Suppliers don't quietly move offices. If the address on the invoice doesn't match your master data, something changed and nobody told you. Find out why before you approve.",
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-23',
    type: 'invoice-master-data',
    shortLabel: 'Address Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-address',
    invoiceMasterData: {
      invoice: {
        supplierName: 'MediaPro GmbH',
        address: 'Kantstraße 34, 10627 Berlin',
        vatId: 'DE315628409',
        iban: 'DE82 1001 0010 0932 4567 00',
        invoiceNumber: 'INV-2025-0731',
        date: '10.06.2025',
        description: 'Content production services - June 2025',
        amount: '€ 9,200.00',
      },
      masterData: {
        supplierName: 'MediaPro GmbH',
        address: 'Kurfürstendamm 34, 10627 Berlin',
        vatId: 'DE315628409',
        iban: 'DE82 1001 0010 0932 4567 00',
      },
      correctFieldId: 'invoice-address',
    },
    mossInsight: {
      body: "Suppliers don't quietly move offices. If the address on the invoice doesn't match your master data, something changed and nobody told you. Find out why before you approve.",
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-24',
    type: 'invoice-master-data',
    shortLabel: 'Address Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-address',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Dubois & Associés S.A.S.',
        address: '28 Rue de la Paix, 75002 Paris',
        vatId: 'FR28453812097',
        iban: 'FR76 3000 4028 3798 7654 3210 943',
        invoiceNumber: 'INV-2025-0556',
        date: '08.07.2025',
        description: 'Business consulting services - July 2025',
        amount: '€ 6,300.00',
      },
      masterData: {
        supplierName: 'Dubois & Associés S.A.S.',
        address: '14 Rue de la Paix, 75002 Paris',
        vatId: 'FR28453812097',
        iban: 'FR76 3000 4028 3798 7654 3210 943',
      },
      correctFieldId: 'invoice-address',
    },
    mossInsight: {
      body: "Suppliers don't quietly move offices. If the address on the invoice doesn't match your master data, something changed and nobody told you. Find out why before you approve.",
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-25',
    type: 'invoice-master-data',
    shortLabel: 'VAT ID Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-vat-id',
    invoiceMasterData: {
      invoice: {
        supplierName: 'CloudSystems GmbH',
        address: 'Rosenthaler Straße 40, 10178 Berlin',
        vatId: 'DE285391047',
        iban: 'DE55 5001 0517 4837 2910 28',
        invoiceNumber: 'INV-2025-0634',
        date: '14.03.2025',
        description: 'Cloud infrastructure services - March 2025',
        amount: '€ 15,400.00',
      },
      masterData: {
        supplierName: 'CloudSystems GmbH',
        address: 'Rosenthaler Straße 40, 10178 Berlin',
        vatId: 'DE285931047',
        iban: 'DE55 5001 0517 4837 2910 28',
      },
      correctFieldId: 'invoice-vat-id',
    },
    mossInsight: {
      body: 'An incorrect VAT ID can invalidate input VAT deduction, even if the invoice looks otherwise correct.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-26',
    type: 'invoice-master-data',
    shortLabel: 'VAT ID Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-vat-id',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Syntegra B.V.',
        address: 'Zuidas 87, 1082 ME Amsterdam',
        vatId: 'BE0823749165',
        iban: 'NL55 INGB 0004 5678 91',
        invoiceNumber: 'INV-2025-1092',
        date: '20.04.2025',
        description: 'Data analytics services - Q2 2025',
        amount: '€ 8,900.00',
      },
      masterData: {
        supplierName: 'Syntegra B.V.',
        address: 'Zuidas 87, 1082 ME Amsterdam',
        vatId: 'NL823749165B01',
        iban: 'NL55 INGB 0004 5678 91',
      },
      correctFieldId: 'invoice-vat-id',
    },
    mossInsight: {
      body: 'An incorrect VAT ID can invalidate input VAT deduction, even if the invoice looks otherwise correct.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-27',
    type: 'invoice-master-data',
    shortLabel: 'VAT ID Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-vat-id',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Sterling Advisory Ltd',
        address: '7 Bishopsgate, London EC2M 3UZ',
        vatId: 'GB341357291',
        iban: 'GB29 HSBC 4001 5050 3748 20',
        invoiceNumber: 'INV-2025-3341',
        date: '08.05.2025',
        description: 'Financial advisory services - Q2 2025',
        amount: '£ 11,600.00',
      },
      masterData: {
        supplierName: 'Sterling Advisory Ltd',
        address: '7 Bishopsgate, London EC2M 3UZ',
        vatId: 'GB341357290',
        iban: 'GB29 HSBC 4001 5050 3748 20',
      },
      correctFieldId: 'invoice-vat-id',
    },
    mossInsight: {
      body: 'An incorrect VAT ID can invalidate input VAT deduction, even if the invoice looks otherwise correct.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-28',
    type: 'invoice-master-data',
    shortLabel: 'VAT ID Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-vat-id',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Technik & Beratung AG',
        address: 'Maximilianstraße 12, 80539 München',
        vatId: 'DE274519381',
        iban: 'DE84 7002 0270 0129 3748 00',
        invoiceNumber: 'INV-2025-0872',
        date: '30.05.2025',
        description: 'Engineering consulting services - May 2025',
        amount: '€ 22,300.00',
      },
      masterData: {
        supplierName: 'Technik & Beratung AG',
        address: 'Maximilianstraße 12, 80539 München',
        vatId: 'DE274519380',
        iban: 'DE84 7002 0270 0129 3748 00',
      },
      correctFieldId: 'invoice-vat-id',
    },
    mossInsight: {
      body: 'An incorrect VAT ID can invalidate input VAT deduction, even if the invoice looks otherwise correct.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-29',
    type: 'invoice-master-data',
    shortLabel: 'VAT ID Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-vat-id',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Proviant Logistics B.V.',
        address: 'Westerdoksdijk 40, 1013 AE Amsterdam',
        vatId: 'NL815637290B02',
        iban: 'NL73 RABO 0456 7891 23',
        invoiceNumber: 'INV-2025-0447',
        date: '16.06.2025',
        description: 'Transport & logistics services - June 2025',
        amount: '€ 7,100.00',
      },
      masterData: {
        supplierName: 'Proviant Logistics B.V.',
        address: 'Westerdoksdijk 40, 1013 AE Amsterdam',
        vatId: 'NL815637290B01',
        iban: 'NL73 RABO 0456 7891 23',
      },
      correctFieldId: 'invoice-vat-id',
    },
    mossInsight: {
      body: 'An incorrect VAT ID can invalidate input VAT deduction, even if the invoice looks otherwise correct.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-30',
    type: 'invoice-master-data',
    shortLabel: 'VAT ID Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-vat-id',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Rhein Digital GmbH',
        address: 'Hohe Straße 28, 50667 Köln',
        vatId: 'NL289347610B01',
        iban: 'DE18 3706 0120 0123 4567 89',
        invoiceNumber: 'INV-2025-1203',
        date: '04.07.2025',
        description: 'Software development services - July 2025',
        amount: '€ 18,500.00',
      },
      masterData: {
        supplierName: 'Rhein Digital GmbH',
        address: 'Hohe Straße 28, 50667 Köln',
        vatId: 'DE289347610',
        iban: 'DE18 3706 0120 0123 4567 89',
      },
      correctFieldId: 'invoice-vat-id',
    },
    mossInsight: {
      body: 'An incorrect VAT ID can invalidate input VAT deduction, even if the invoice looks otherwise correct.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-31',
    type: 'invoice-master-data',
    shortLabel: 'VAT ID Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-vat-id',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Pinnacle Ventures Ltd',
        address: '4 New Street Square, London EC4A 3TW',
        vatId: 'GB298204572',
        iban: 'GB41 LLOY 3094 7618 3920 22',
        invoiceNumber: 'INV-2025-4821',
        date: '11.08.2025',
        description: 'Business development consulting - Q3 2025',
        amount: '£ 9,400.00',
      },
      masterData: {
        supplierName: 'Pinnacle Ventures Ltd',
        address: '4 New Street Square, London EC4A 3TW',
        vatId: 'GB298204573',
        iban: 'GB41 LLOY 3094 7618 3920 22',
      },
      correctFieldId: 'invoice-vat-id',
    },
    mossInsight: {
      body: 'An incorrect VAT ID can invalidate input VAT deduction, even if the invoice looks otherwise correct.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-32',
    type: 'invoice-master-data',
    shortLabel: 'VAT ID Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-vat-id',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Nexoria GmbH',
        address: 'Taunusanlage 8, 60329 Frankfurt am Main',
        vatId: 'DE312847093',
        iban: 'DE24 5008 0000 0123 4567 00',
        invoiceNumber: 'INV-2025-0991',
        date: '25.09.2025',
        description: 'Market research services - Q3 2025',
        amount: '€ 5,700.00',
      },
      masterData: {
        supplierName: 'Nexoria GmbH',
        address: 'Taunusanlage 8, 60329 Frankfurt am Main',
        vatId: 'DE312847092',
        iban: 'DE24 5008 0000 0123 4567 00',
      },
      correctFieldId: 'invoice-vat-id',
    },
    mossInsight: {
      body: 'An incorrect VAT ID can invalidate input VAT deduction, even if the invoice looks otherwise correct.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-33',
    type: 'invoice-master-data',
    shortLabel: 'IBAN Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-iban',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Digitech Solutions GmbH',
        address: 'Bismarckstraße 11, 10625 Berlin',
        vatId: 'DE318274059',
        iban: 'NL43 INGB 0004 5872 93',
        invoiceNumber: 'INV-2025-0742',
        date: '20.01.2025',
        description: 'IT infrastructure support - January 2025',
        amount: '€ 13,800.00',
      },
      masterData: {
        supplierName: 'Digitech Solutions GmbH',
        address: 'Bismarckstraße 11, 10625 Berlin',
        vatId: 'DE318274059',
        iban: 'DE89 1002 0890 0048 7239 00',
      },
      correctFieldId: 'invoice-iban',
    },
    mossInsight: {
      body: 'A changed IBAN is the #1 trigger for payment fraud, most invoice scams don’t change the invoice, just the bank details.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-34',
    type: 'invoice-master-data',
    shortLabel: 'IBAN Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-iban',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Benelux Trade B.V.',
        address: 'Prinses Margrietplantsoen 33, 2595 AM Den Haag',
        vatId: 'NL857234091B01',
        iban: 'NL82 ABNA 0415 7863 92',
        invoiceNumber: 'INV-2025-0523',
        date: '14.02.2025',
        description: 'Import consulting services - Q1 2025',
        amount: '€ 6,200.00',
      },
      masterData: {
        supplierName: 'Benelux Trade B.V.',
        address: 'Prinses Margrietplantsoen 33, 2595 AM Den Haag',
        vatId: 'NL857234091B01',
        iban: 'NL82 ABNA 0415 7863 91',
      },
      correctFieldId: 'invoice-iban',
    },
    mossInsight: {
      body: 'A changed IBAN is the #1 trigger for payment fraud, most invoice scams don’t change the invoice, just the bank details.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-35',
    type: 'invoice-master-data',
    shortLabel: 'IBAN Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-iban',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Albion Finance Ltd',
        address: '100 Wood Street, London EC2V 7AN',
        vatId: 'GB427198350',
        iban: 'IE29 AIBK 9311 5212 3456 78',
        invoiceNumber: 'INV-2025-5501',
        date: '03.03.2025',
        description: 'Financial compliance services - Q1 2025',
        amount: '£ 14,200.00',
      },
      masterData: {
        supplierName: 'Albion Finance Ltd',
        address: '100 Wood Street, London EC2V 7AN',
        vatId: 'GB427198350',
        iban: 'GB45 BARC 2052 7153 7920 81',
      },
      correctFieldId: 'invoice-iban',
    },
    mossInsight: {
      body: 'A changed IBAN is the #1 trigger for payment fraud, most invoice scams don’t change the invoice, just the bank details.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-36',
    type: 'invoice-master-data',
    shortLabel: 'IBAN Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-iban',
    invoiceMasterData: {
      invoice: {
        supplierName: 'BioTech Consulting GmbH',
        address: 'Zeppelinstraße 1, 81669 München',
        vatId: 'DE293841075',
        iban: 'DE21 7012 0400 2019 3748 22',
        invoiceNumber: 'INV-2025-0784',
        date: '19.04.2025',
        description: 'R&D; consulting services - April 2025',
        amount: '€ 28,600.00',
      },
      masterData: {
        supplierName: 'BioTech Consulting GmbH',
        address: 'Zeppelinstraße 1, 81669 München',
        vatId: 'DE293841075',
        iban: 'DE21 7012 0400 2019 3748 21',
      },
      correctFieldId: 'invoice-iban',
    },
    mossInsight: {
      body: 'A changed IBAN is the #1 trigger for payment fraud, most invoice scams don’t change the invoice, just the bank details.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-37',
    type: 'invoice-master-data',
    shortLabel: 'IBAN Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-iban',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Deltastream B.V.',
        address: 'Laan van Kronenburg 14, 1183 AS Amstelveen',
        vatId: 'NL829147360B01',
        iban: 'DE85 5004 0000 0123 4567 00',
        invoiceNumber: 'INV-2025-0871',
        date: '07.05.2025',
        description: 'Software integration services - Q2 2025',
        amount: '€ 11,300.00',
      },
      masterData: {
        supplierName: 'Deltastream B.V.',
        address: 'Laan van Kronenburg 14, 1183 AS Amstelveen',
        vatId: 'NL829147360B01',
        iban: 'NL57 RABO 0301 9457 28',
      },
      correctFieldId: 'invoice-iban',
    },
    mossInsight: {
      body: 'A changed IBAN is the #1 trigger for payment fraud, most invoice scams don’t change the invoice, just the bank details.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-38',
    type: 'invoice-master-data',
    shortLabel: 'IBAN Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-iban',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Whitfield & Partners Ltd',
        address: '15 Finsbury Square, London EC2A 1NT',
        vatId: 'GB391274850',
        iban: 'GB39 NWBK 6016 1331 9268 55',
        invoiceNumber: 'INV-2025-7734',
        date: '16.06.2025',
        description: 'Corporate legal services - Q2 2025',
        amount: '£ 19,500.00',
      },
      masterData: {
        supplierName: 'Whitfield & Partners Ltd',
        address: '15 Finsbury Square, London EC2A 1NT',
        vatId: 'GB391274850',
        iban: 'GB39 NWBK 6016 1331 9268 54',
      },
      correctFieldId: 'invoice-iban',
    },
    mossInsight: {
      body: 'A changed IBAN is the #1 trigger for payment fraud, most invoice scams don’t change the invoice, just the bank details.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-39',
    type: 'invoice-master-data',
    shortLabel: 'IBAN Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-iban',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Markgrafen Capital GmbH',
        address: 'Unter den Linden 5, 10117 Berlin',
        vatId: 'DE287493165',
        iban: 'DE57 2004 0033 0623 4798 00',
        invoiceNumber: 'INV-2025-1039',
        date: '30.07.2025',
        description: 'M&A; advisory services - July 2025',
        amount: '€ 45,000.00',
      },
      masterData: {
        supplierName: 'Markgrafen Capital GmbH',
        address: 'Unter den Linden 5, 10117 Berlin',
        vatId: 'DE287493165',
        iban: 'DE41 1001 0010 0093 4567 00',
      },
      correctFieldId: 'invoice-iban',
    },
    mossInsight: {
      body: 'A changed IBAN is the #1 trigger for payment fraud, most invoice scams don’t change the invoice, just the bank details.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
  {
    id: 'mdc-40',
    type: 'invoice-master-data',
    shortLabel: 'IBAN Check',
    phase: 2,
    questionGroup: 'q13-invoice-master-data',
    timerSeconds: 45,
    points: 10,
    question: 'Compare the invoice against the supplier master data.',
    options: [],
    correctAnswer: 'invoice-iban',
    invoiceMasterData: {
      invoice: {
        supplierName: 'Quanta Innovations B.V.',
        address: 'High Tech Campus 5, 5656 AE Eindhoven',
        vatId: 'NL804726153B01',
        iban: 'BE68 5390 0754 7034',
        invoiceNumber: 'INV-2025-0229',
        date: '12.09.2025',
        description: 'Technology R&D; services - Q3 2025',
        amount: '€ 9,800.00',
      },
      masterData: {
        supplierName: 'Quanta Innovations B.V.',
        address: 'High Tech Campus 5, 5656 AE Eindhoven',
        vatId: 'NL804726153B01',
        iban: 'NL27 INGB 0002 7348 91',
      },
      correctFieldId: 'invoice-iban',
    },
    mossInsight: {
      body: 'A changed IBAN is the #1 trigger for payment fraud, most invoice scams don’t change the invoice, just the bank details.',
      howMossHelps:
        'Moss automatically compares every invoice against trusted supplier master data flagging mismatches in IBAN, VAT ID, or entity details in real time, before approval or payment.',
    },
  },
]
